import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import HappySchoolCellSubstituteDialog from "./HappySchoolCellSubstituteDialog";
import HappySchoolCellAssignDialog from "./HappySchoolCellAssignDialog";
import HappySchoolCellSwapDialog from "./HappySchoolCellSwapDialog";
import { useRoomAssignment, RoomRow } from "./roomAssignment";
import {
  getTimetableRun,
  listClasses,
  listSubjects,
  listStaff,
  getPeriodTimes,
  getVocationalSchedule,
  getVanhoaDistribution,
  getWeekSubjectPlan,
  getStaffBusyWeek,
  getWeekMaxPerDay,
  getPeriodNotes,
  updateTimetableRunCell,
  getTimetableRunDraft,
  approveTimetableRunDraft,
  discardTimetableRunDraft,
  fillKhoiSubstitute,
  getMyStaff,
  TEACHER_DEPARTMENT,
  getClassSubjectsByWeek,
  listTimetableRuns,
  getPublishLog,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import { periodShadeIndex } from "./PeriodShadeStyle";

// Webview nhúng trong Zalo/Messenger/Instagram/Line/WeChat CHỈ chặn tải file
// (Blob download) trên iOS (WKWebView giới hạn) — Android webview vẫn tải
// bình thường qua Download Manager của hệ thống nên không cần chặn.
function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}

// XLSX.writeFile() dùng cơ chế tải file mặc định của thư viện — trên nhiều
// trình duyệt di động (Safari iOS, WebView trong app) hay bị im lặng không
// tải được gì. Tự dựng Blob + thẻ <a download> gắn vào DOM rồi click là
// cách tương thích rộng nhất trên mobile.
function downloadWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Xuất Excel lại."
    );
    return;
  }
  const wbArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// Xuất workbook ExcelJS (khác wb của thư viện "xlsx" ở trên) — dùng khi cần
// style thật (font, kẻ ô...) mà bản "xlsx" (SheetJS community) không hỗ trợ.
async function downloadExcelJsWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Xuất Excel lại."
    );
    return;
  }
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// Áp Times New Roman + kẻ ô (viền mỏng) cho MỌI ô đã có dữ liệu trong 1 sheet.
function styleSheetTimesNewRomanBordered(ws) {
  ws.eachRow({ includeEmpty: true }, (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { name: "Times New Roman", size: 12, bold: cell.font && cell.font.bold };
      cell.border = {
        top: { style: "thin" }, left: { style: "thin" },
        bottom: { style: "thin" }, right: { style: "thin" },
      };
    });
  });
}
function downloadAoa(aoa, sheetName, fileName) {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  downloadWorkbook(wb, fileName);
}
// Tên sheet Excel: tối đa 31 ký tự, không được chứa : \ / ? * [ ]
function safeSheetName(name) {
  return String(name).replace(/[:\\/?*[\]]/g, "-").slice(0, 31);
}
const slug = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "tkb";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
// Đơn giá chấm công dạy văn hoá — CHỈ áp dụng cho GV Thỉnh giảng (Cơ hữu ăn
// lương cố định, đơn giá theo tiết = 0đ). Tiết Tăng cường (TC) và tiết
// thường (chính khoá) tách riêng đơn giá, không gộp chung 1 mức như trước.
const UNIT_PRICE_THUONG = 80000; // đơn giá 1 tiết THƯỜNG (Thỉnh giảng)
const UNIT_PRICE_TC = 130000; // đơn giá 1 tiết TĂNG CƯỜNG (Thỉnh giảng) — Level 2
const UNIT_PRICE_TC_L1 = 80000; // tiết TC của GV Thỉnh giảng Level 1 (từ TG_LEVEL_FROM)
const TG_LEVEL_FROM = "2026-09-01"; // khớp happyschool-api/src/routes/timetable.js

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").toLowerCase().replace(/[^a-z0-9]/g, "");
// Viết tắt tên môn cho bảng "Toàn trường" (khớp tên sheet TKB_GiaoVien: VĂN-HỒNG…).
const SUBJ_ABBR = { nguvan: "VĂN", toan: "TOÁN", gdktpl: "GDKTPL", congnghe: "CN", vatly: "LÝ", hoahoc: "HÓA", sinhhoc: "SINH", lichsu: "SỬ", dialy: "ĐỊA", tienganh: "ANH", phapluat: "PL" };
const subjAbbr = (name) => SUBJ_ABBR[norm(name)] || String(name || "").split(/\s+/).map((w) => w[0] || "").join("").toUpperCase();
const lastWord = (name) => { const p = String(name || "").trim().split(/\s+/); return (p[p.length - 1] || "").toUpperCase(); };

// startDate "YYYY-MM-DD" + n ngày -> "DD/MM"
function dayDM(startDate, n) {
  if (!startDate) return "";
  const [y, m, d] = String(startDate).split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;
}

// Chuyển 1 khoá "lịch bận" thành danh sách (day, period toàn cục) — hỗ trợ
// "d_p" (tiết cụ thể, vd "2_5") và "d_S"/"d_C" (cả buổi) — khớp
// happyschool-api/src/routes/timetable.js busyKeyToPairs.
function busyKeyToGlobalPairs(key, sessions) {
  const [dayStr, rest] = String(key).split("_");
  const day = Number(dayStr);
  if (Number.isNaN(day)) return [];
  if (rest === "S" || rest === "C") {
    const si = rest === "S" ? 0 : 1;
    const ses = sessions[si];
    return ses ? ses.periods.map((p) => [day, p.global]) : [];
  }
  const period = Number(rest);
  if (Number.isNaN(period)) return [];
  return [[day, period]];
}
const khoiOf = (name) => (String(name || "").match(/^(\d+)/) || [])[1];

function buildSessions(pt) {
  let cur = 0;
  return (pt && pt.sessions ? pt.sessions : []).map((s) => {
    const periods = (s.periods || []).map((time, i) => ({ global: cur + i, idx: i, time }));
    cur += (s.periods || []).length;
    return { key: s.key, label: s.label, periods };
  });
}
// Tách chuỗi "Ngày học" của nghề -> danh sách {d, s} (s: 0=sáng,1=chiều).
function ngheSlots(dayStr) {
  const str = String(dayStr).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const out = [];
  const re = /(sang|chieu|ngay)\s+thu\s+(\d)/g;
  let m;
  while ((m = re.exec(str))) {
    const dow = Number(m[2]);
    if (dow < 2 || dow > 7) continue;
    const d = dow - 2;
    (m[1] === "sang" ? [0] : m[1] === "chieu" ? [1] : [0, 1]).forEach((s) => out.push({ d, s }));
  }
  return out;
}

// mode: "vh" | "vhnghe" | "teacher" | "student" | "studentOnly" | "chamcong"
// defaultTeacherId: ở mode "teacher", ưu tiên chọn sẵn GV này thay vì phần tử đầu (vd GV tự xem TKB của mình).
// lockTeacher: true khi GV tự xem TKB của chính mình -> ẩn hẳn combobox chọn GV, chỉ hiện đúng TKB của họ.
// isOwner: true khi màn hình được chủ trường/admin mở (HappySchoolTKBManager) -> cho phép xử lý âm tiết ở mode "student".
const HappySchoolTimetableVersion = ({ runId, weekId, mode, khois, startDate, defaultTeacherId, selfTeacher, lockTeacher, isOwner, isAdmin = false }) => {
  const schoolId = getActiveSchoolId();
  const [run, setRun] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [voc, setVoc] = useState({ rows: [] });
  const [vhDist, setVhDist] = useState({ rows: [] }); // vanhoaDistribution: rows[].{className, cells:[{sang,chieu}]x6} — biết ô nào thật sự là buổi văn hoá
  const [plan, setPlan] = useState({}); // weekSubjectPlan: { classId: { subjectId: periodsPerWeek } } — "cần" theo Cấu hình môn học
  const [busyWeek, setBusyWeek] = useState({}); // { teacherId: { "d_p"|"d_S"|"d_C": true } } — lịch bận tất cả GV, dùng tô đỏ/xanh khi sửa
  const [weekMaxPerDay, setWeekMaxPerDay] = useState({}); // { classId: { subjectId: maxPerDay } } — trần tiết/ngày riêng theo tuần, dùng khi "Xếp bù tiết thiếu"
  const [pccmWeek, setPccmWeek] = useState({}); // PCCM hiệu lực của tuần (đã gộp) — GV chính từng lớp-môn, ai dạy khác thì ghi "Dạy thay"
  const [periodNotes, setPeriodNotes] = useState({}); // { classId: { "day_period": {note, teacherId?} } } — ghi chú riêng từng ô, hiện trên TKB GV
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chamcongTab, setChamcongTab] = useState("total"); // "total" | "day_si"
  const [editMode, setEditMode] = useState(false); // chế độ Sửa (chỉ mode "vh") — ô GV thành combobox
  const [pendingEdits, setPendingEdits] = useState({}); // key -> teacherId, sửa tại chỗ CHƯA bấm Lưu
  const [draft, setDraft] = useState({}); // key -> [subjectId, teacherId], ĐÃ Lưu vào nháp nhưng CHƯA Duyệt
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [khoiSubBusy, setKhoiSubBusy] = useState(false);
  const [fillDialogOpen, setFillDialogOpen] = useState(false); // dialog "Xếp bù tiết thiếu" (mode "student", isOwner)
  const [fillSubjectId, setFillSubjectId] = useState("");
  const [fillTeacherId, setFillTeacherId] = useState("");
  const [fillBusy, setFillBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0); // tăng lên để tải lại bản sau khi admin xử lý 1 ô
  const [assignTarget, setAssignTarget] = useState(null); // {teacherId, day, period} — ô TRỐNG của GV mở dialog điền môn+lớp (nhấn đúp, chỉ admin, mode "teacher")
  const [subTarget, setSubTarget] = useState(null); // {classId, day, period} — ô mở dialog thay GV (nhấn đúp, chỉ admin, mode "teacher")
  // (24/10/2026) ĐỔI TIẾT ở "TKB cho GV & Học sinh" (từng lớp): GV bấm tiết của mình, admin/BGH bấm tiết bất kỳ.
  const [swapTarget, setSwapTarget] = useState(null); // {classId, day, period}
  const [myStaffId, setMyStaffId] = useState(defaultTeacherId || "");
  // Thông tin BẢN CÔNG BỐ đang xem (in kèm trên bảng Chấm công): số bản + người công bố (người công bố chỉ admin/BGH đọc được log).
  const [banNo, setBanNo] = useState("");
  const [banBy, setBanBy] = useState("");
  useEffect(() => {
    if (!schoolId || !runId || !weekId) return;
    let stale = false;
    listTimetableRuns(schoolId, weekId)
      .then((list) => { if (stale) return; const l = list || []; const i = l.findIndex((r) => r.id === runId); setBanNo(i >= 0 ? `Bản ${l.length - i}` : ""); })
      .catch(() => { if (!stale) setBanNo(""); });
    getPublishLog(schoolId, { kind: "tkb", weekId, limit: 100 })
      .then((d) => { if (stale) return; const e = (d.entries || []).find((x) => x.runId === runId && x.action === "publish" && !x.backfilled && x.byName); setBanBy(e ? e.byName : ""); })
      .catch(() => { if (!stale) setBanBy(""); });
    return () => { stale = true; };
  }, [schoolId, runId, weekId, run && run.publishedAt]); // eslint-disable-line
  useEffect(() => {
    if (defaultTeacherId) { setMyStaffId(defaultTeacherId); return; }
    if (!schoolId || mode !== "student") return;
    getMyStaff(schoolId).then((me) => setMyStaffId((me && me.staffId) || "")).catch(() => setMyStaffId(""));
  }, [schoolId, mode, defaultTeacherId]);

  useEffect(() => {
    if (!schoolId || !runId) return;
    setLoading(true);
    // Đổi bản/tuần liên tiếp: lần nạp CŨ (đến muộn) không được ghi đè dữ liệu của bản/tuần MỚI.
    let stale = false;
    Promise.all([
      getTimetableRun(schoolId, runId),
      listClasses(schoolId),
      listSubjects(schoolId),
      // (24/10/2026) + cán bộ khối khác (vd Phòng đào tạo làm GVCN, dạy SH) — trước chỉ tải GV THPT nên ô SH của họ không hiện tên.
      Promise.all([listStaff(schoolId, { department: TEACHER_DEPARTMENT }), listStaff(schoolId).catch(() => [])]),
      getPeriodTimes(schoolId),
      weekId ? getVocationalSchedule(schoolId, weekId) : Promise.resolve({ rows: [] }),
      weekId ? getVanhoaDistribution(schoolId, weekId) : Promise.resolve({ rows: [] }),
      weekId ? getWeekSubjectPlan(schoolId, weekId) : Promise.resolve({ plan: {} }),
      weekId ? getStaffBusyWeek(schoolId, weekId) : Promise.resolve({}),
      weekId ? getWeekMaxPerDay(schoolId, weekId) : Promise.resolve({ plan: {} }),
      getTimetableRunDraft(schoolId, runId),
      weekId ? getPeriodNotes(schoolId, weekId, runId).catch(() => ({})) : Promise.resolve({}),
      weekId ? getClassSubjectsByWeek(schoolId, weekId).catch(() => ({})) : Promise.resolve({}),
    ])
      .then(([r, c, s, tPair, pt, v, vd, wp, bw, mpd, dr, pn, pw]) => {
        if (stale) return;
        setPccmWeek((pw && pw.effective) || {});
        const [tDept, tAllStaff] = tPair;
        const usedIds = new Set([...Object.values((r && r.grid) || {}).map((x) => Array.isArray(x) ? x[1] : null), ...(c || []).map((x) => x.homeroomTeacherId)].filter(Boolean));
        const deptIds = new Set(tDept.map((x) => x.id));
        const t = [...tDept, ...(tAllStaff || []).filter((x) => !deptIds.has(x.id) && usedIds.has(x.id) && x.name)];
        setRun(r);
        setClasses(c.slice().sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true })));
        setSubjects(s);
        // GV đang đăng nhập nhưng hồ sơ KHÔNG nằm ở khối GV THPT (vd GV Nghề thuộc phòng Hành chính / Đào tạo)
        // -> vẫn cộng vào danh sách để TKB của chính họ (nghề theo tên trong lịch nghề) hiện được.
        const tAll = selfTeacher && selfTeacher.id && !t.some((x) => x.id === selfTeacher.id) ? [...t, selfTeacher] : t;
        setTeachers(tAll.slice().sort((a, b) => a.name.localeCompare(b.name, "vi")));
        setSessions(buildSessions(pt));
        setVoc(v || { rows: [] });
        setVhDist(vd || { rows: [] });
        setPlan((wp && wp.plan) || {});
        setBusyWeek(bw || {});
        setWeekMaxPerDay((mpd && mpd.plan) || {});
        setDraft(dr || {});
        setPeriodNotes(pn || {});
        setPendingEdits({});
      })
      .catch((err) => { if (!stale) setError(err.message); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [schoolId, runId, weekId, reloadKey, selfTeacher && selfTeacher.id]); // eslint-disable-line

  const subjectName = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  // (04/11/2026) Phòng học theo tuần: TKB lớp có dòng "🚪 Phòng" đầu mỗi buổi; TKB GV ghi phòng cạnh tiết.
  const { roomOf, hasAny: hasRooms } = useRoomAssignment(schoolId, weekId);
  const teacherName = useMemo(() => Object.fromEntries(teachers.map((t) => [t.id, t.name])), [teachers]);
  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);
  // Chỉ dùng cho "TKB giáo viên": tiết GV đang xem mà KHÁC GV chính (PCCM) của lớp-môn đó thì ghi "Dạy thay". Áp dụng cả Tiếng anh; chỉ bỏ HĐTN (Sinh hoạt/Chào cờ do GVCN, không thuộc PCCM).
  const isDayThay = (classId, sid, tid) => {
    if (!tid || !sid) return false;
    const sn = subjectName[sid] || "";
    if (/^HĐTN/.test(sn)) return false;
    const w = pccmWeek && pccmWeek[classId] && pccmWeek[classId][sid];
    let main = (w && w.assignedTeacherId) || "";
    if (!main) { const f = ((classById[classId] || {}).subjects || []).find((x) => x.subjectId === sid); main = (f && f.assignedTeacherId) || ""; }
    return !!main && main !== tid;
  };
  // className -> {cells:[{sang,chieu}]x6} — tra 1 ô (day, buổi) có phải văn hoá thật không, tránh nhét bù nhầm vào ngày "nghỉ"/"nghề".
  const vhDistByClassName = useMemo(() => Object.fromEntries((vhDist.rows || []).map((r) => [r.className, r])), [vhDist]);
  const isVanhoaSlot = (className, day, si) => {
    const row = vhDistByClassName[className];
    if (!row || !row.cells || !row.cells[day]) return false;
    return row.cells[day][si === 0 ? "sang" : "chieu"] === "vanhoa";
  };
  // Buổi này CÓ đúng là buổi nghề theo bảng phân phối nghề-văn hoá (chuẩn)
  // không — TKB nghề (vocationalSchedule) đôi khi có thêm dòng của 1 lớp
  // nghề/chương trình khác (vd Mê Linh) không thuộc đúng buổi nghề chính
  // thức của lớp đó; nếu không chặn theo bảng chuẩn, các dòng thừa này sẽ bị
  // hiện nhầm thành "HN" ở đúng buổi lẽ ra là văn hoá (đã gặp thực tế 10A11
  // Thứ 2 sáng: bảng chuẩn ghi "vanhoa" nhưng vẫn có dòng nghề Mê Linh).
  const isNgheSlot = (className, day, si) => {
    const row = vhDistByClassName[className];
    if (!row || !row.cells || !row.cells[day]) return false;
    return row.cells[day][si === 0 ? "sang" : "chieu"] === "nghe";
  };

  // GV có đang khai bận đúng (ngày, tiết toàn cục) này không — dùng tô đỏ/xanh.
  const isTeacherBusyAt = (teacherId, day, global) => {
    const b = busyWeek[teacherId];
    if (!b) return false;
    return Object.keys(b).some((k) => busyKeyToGlobalPairs(k, sessions).some(([d, p]) => d === day && p === global));
  };

  // môn -> danh sách {classId, className, teacherId} GV chính đang dạy môn đó,
  // dùng gợi ý combobox: chính lớp trước, cùng khối sau, còn lại cuối.
  const teachersOfSubject = useMemo(() => {
    const map = {}; // subjectId -> [{classId, className, teacherId}]
    classes.forEach((c) => {
      (c.subjects || []).forEach((s) => {
        if (!s.assignedTeacherId) return;
        map[s.subjectId] = map[s.subjectId] || [];
        map[s.subjectId].push({ classId: c.id, className: c.name, teacherId: s.assignedTeacherId });
        (s.poolTeacherIds || []).forEach((tid) => {
          map[s.subjectId].push({ classId: c.id, className: c.name, teacherId: tid });
        });
      });
    });
    return map;
  }, [classes]);

  // Danh sách GV gợi ý cho combobox sửa 1 ô (classId, subjectId): GV của
  // chính lớp đó trước, rồi GV cùng khối dạy môn đó, rồi các GV còn lại
  // trong trường dạy môn đó — không trùng lặp.
  const teacherCandidatesFor = (classId, subjectId) => {
    const cls = classById[classId];
    const khoi = khoiOf(cls && cls.name);
    const entries = teachersOfSubject[subjectId] || [];
    const seen = new Set();
    const ordered = [];
    const pushUnique = (tid) => { if (tid && !seen.has(tid)) { seen.add(tid); ordered.push(tid); } };
    entries.filter((e) => e.classId === classId).forEach((e) => pushUnique(e.teacherId));
    entries.filter((e) => e.classId !== classId && khoiOf(e.className) === khoi).forEach((e) => pushUnique(e.teacherId));
    entries.filter((e) => khoiOf(e.className) !== khoi).forEach((e) => pushUnique(e.teacherId));
    return ordered;
  };
  const activeKhois = Array.isArray(khois) ? khois.filter((k) => ["12", "11", "10"].includes(String(k))) : [];
  const displayClasses = useMemo(
    () => (activeKhois.length > 0 && activeKhois.length < 3
      ? classes.filter((c) => activeKhois.includes((String(c.name).match(/^(\d+)/) || [])[1]))
      : classes),
    [classes, khois] // eslint-disable-line
  );
  const periodsPerDay = sessions.reduce((n, s) => n + s.periods.length, 0);
  const sessionOfGlobal = (g) => sessions.findIndex((s) => s.periods.some((p) => p.global === g));
  // Học nghề mỗi buổi chỉ học 4 tiết (1-4), KHÔNG có tiết 5 — dù bảng giờ có
  // 5 tiết/buổi (dành cho văn hoá), tiết 5 luôn bỏ trống bên nghề.
  const NGHE_MAX_IDX = 3;
  const periodIdxInSession = (si, g) => {
    const p = ((sessions[si] || {}).periods || []).find((pp) => pp.global === g);
    return p ? p.idx : -1;
  };

  const ngheMap = useMemo(() => {
    const map = {};
    (voc.rows || []).forEach((r) => {
      ngheSlots(r.day).forEach(({ d, s }) => {
        map[`${r.className}|${d}|${s}`] = { subject: r.subject, teacher: r.teacher, room: r.room };
      });
    });
    return map;
  }, [voc]);
  // Lớp giải tán khi học văn hoá (học sinh dồn sang lớp khác) nhưng vẫn tập hợp lại học nghề: không có ô "HN" trong lưới
  // và không có dòng trong bảng phân phối, nên buổi nghề của lớp này lấy thẳng từ lịch nghề.
  const giaiTanNames = useMemo(() => new Set(classes.filter((c) => c.status === "giai_tan").map((c) => c.name)), [classes]);

  // Lịch nghề chỉ ghi tên GV dạng chuỗi tự do (có thể có "Cô "/"Thầy " ở đầu),
  // không có staffId — phải so khớp theo tên (bỏ dấu, bỏ kính ngữ) để hiện
  // đúng TKB cho GV dạy nghề trên màn hình "TKB Giáo viên".
  const stripHonorific = (s) => String(s || "").replace(/^\s*(cô giáo|thầy giáo|cô|thầy)\s+/i, "").trim();
  const ngheByTeacherName = useMemo(() => {
    const map = {};
    (voc.rows || []).forEach((r) => {
      if (!r.teacher) return;
      const key = norm(stripHonorific(r.teacher));
      ngheSlots(r.day).forEach(({ d, s }) => {
        const k = `${key}|${d}|${s}`;
        (map[k] = map[k] || []).push({ className: r.className, subject: r.subject, room: r.room });
      });
    });
    return map;
  }, [voc]);
  const ngheForTeacher = (teacherId, day, si) => {
    const t = teachers.find((x) => x.id === teacherId);
    if (!t) return null;
    const hits = ngheByTeacherName[`${norm(t.name)}|${day}|${si}`];
    if (!hits) return null;
    // Chỉ nhận đúng lớp/buổi mà bảng phân phối (chuẩn) xác nhận là nghề —
    // TKB nghề đôi khi có thêm dòng của chương trình khác (vd Mê Linh) rơi
    // vào đúng buổi lẽ ra là văn hoá của lớp đó.
    // Lớp KHÔNG có trong lịch phân phối nghề-văn hoá (không có TKB văn hoá) -> tin lịch nghề, hiện cả buổi.
    const validHits = hits.filter((h) => !vhDistByClassName[h.className] || isNgheSlot(h.className, day, si));
    return validHits.length ? validHits : null;
  };

  const grid = (run && run.grid) || {};
  const classIdsInGrid = useMemo(() => new Set(Object.keys(grid).map((k) => k.split("|")[0])), [grid]);
  // Chỉ những GV thực sự có tiết (văn hoá trong grid, HOẶC nghề theo tên
  // trong lịch nghề) trong bản TKB đang xem mới vào combobox — NGOẠI LỆ: luôn
  // cộng thêm chính GV đang đăng nhập (defaultTeacherId) dù tuần/bản xếp này
  // họ không có tiết, để họ luôn thấy đúng tên mình (không rơi về hiện nhầm
  // TKB của GV khác đứng đầu danh sách).
  const teachersWithLessons = useMemo(() => {
    const ids = new Set(Object.values(grid).filter(Boolean).map(([, tId]) => tId));
    const ngheNames = new Set((voc.rows || []).map((r) => norm(stripHonorific(r.teacher || ""))).filter(Boolean));
    const list = teachers.filter((t) => ids.has(t.id) || ngheNames.has(norm(t.name)));
    if (defaultTeacherId && !list.some((t) => t.id === defaultTeacherId)) {
      const me = teachers.find((t) => t.id === defaultTeacherId);
      if (me) list.push(me);
    }
    return list;
  }, [grid, teachers, defaultTeacherId, voc]); // eslint-disable-line

  // Nhãn "(TC)" (Tăng cường) đã được backend TÍNH SẴN VÀ GHI THẲNG vào grid
  // lúc xếp xong (xem markTcCells trong timetable.js) — val[2]===true — mọi
  // nơi hiển thị chỉ cần đọc cờ này, KHÔNG tự tính lại (tránh mỗi chỗ 1 kiểu).
  const isTc = (val) => !!(val && val[2] === true);

  useEffect(() => {
    // GV tự xem TKB của mình: luôn khoá đúng defaultTeacherId, kể cả khi GV
    // này không có tiết nào trong bản xếp đang xem (không có trong danh sách
    // teachersWithLessons) — tuyệt đối không được rơi về hiện nhầm TKB của
    // một GV khác chỉ vì list lọc theo "có tiết" không chứa họ.
    if (mode === "teacher" && lockTeacher && defaultTeacherId) {
      setSelectedId(defaultTeacherId);
      return;
    }
    const list = mode === "teacher" ? teachersWithLessons : displayClasses;
    if (!list.length) return;
    if (list.find((x) => x.id === selectedId)) return;
    const preferred = mode === "teacher" && defaultTeacherId && list.find((x) => x.id === defaultTeacherId);
    setSelectedId(preferred ? preferred.id : list[0].id);
  }, [mode, teachersWithLessons, displayClasses, defaultTeacherId, lockTeacher]); // eslint-disable-line

  if (!runId) return <p style={{ color: "#6b7280" }}>Chưa chọn phiên bản.</p>;
  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (run && run.status !== "done") return <p style={{ color: "#b45309" }}>Phiên bản này chưa xếp xong (trạng thái: {run.status}).</p>;

  const vhAt = (classId, day, g) => grid[`${classId}|${day}|${g}`] || null; // [subId, teaId]
  // Như vhAt nhưng đè thêm bản nháp (đã Lưu, chưa Duyệt) — dùng ở màn "TKB cho GV & Học sinh"
  // để chủ trường thấy ngay các tiết vừa "Xếp bù tiết thiếu" dù chưa Duyệt thành version mới.
  const vhAtEff = (classId, day, g) => {
    const key = `${classId}|${day}|${g}`;
    return draft[key] || grid[key] || null;
  };
  // Nghề LUÔN đúng 4 tiết/buổi (5 tiết thì 1 tiết còn lại là văn hoá thật —
  // có thể ở ĐẦU hay CUỐI buổi tuỳ lớp, không cố định). Ô lưới đã ghi rõ
  // "HN" (backend ghim đúng lúc xếp) mới chắc chắn là 1 trong 4 tiết nghề đó
  // — ô TRỐNG (chưa xếp gì) dù cùng buổi nghề KHÔNG được tự suy ra là nghề,
  // vì có thể là 1 tiết văn hoá thật đang bị thiếu (CP-SAT chưa xếp được),
  // không phải tiết nghề thứ 5 (đã gặp thực tế 10A12 Thứ 6 sáng: chỉ 4 ô
  // "HN", ô còn lại trống thật — nếu suy ra nghề sẽ thành sai thừa 1 tiết).
  // isNgheSlot chỉ để chặn thêm dữ liệu nghề "lạc" của chương trình khác (vd
  // Mê Linh) rơi vào đúng buổi lẽ ra là văn hoá (thực tế 10A11 Thứ 2 sáng).
  const ngheAt = (className, day, si, vhCell) => {
    if (giaiTanNames.has(className)) return ngheMap[`${className}|${day}|${si}`] || null;
    if (!vhCell || vhCell[0] !== "HN") return null;
    if (!isNgheSlot(className, day, si)) return null;
    // (24/10/2026) Buổi nghề chưa nhập lịch nghề chi tiết -> vẫn hiện "Học nghề", không để trống.
    return ngheMap[`${className}|${day}|${si}`] || { subject: "", teacher: "", room: "" };
  };

  // ================= TOÀN TRƯỜNG (VH / VH-Nghề) =================
  if (mode === "vh" || mode === "vhnghe") {
    const withNgheDetail = mode === "vhnghe";
    const cols = displayClasses;
    // GV thực xếp có KHÁC GV chính trong PCCM (assignedTeacherId) không — tức
    // đây là 1 ca "dạy thay" (do kẹt lịch/dạy thay cùng khối...), không phải
    // GV mặc định của lớp-môn đó. Không tính pool (poolTeacherIds) là dạy
    // thay vì đó vẫn là GV đã đăng ký trước cho lớp này. Riêng Tiếng anh:
    // backend dùng chung pool nhiều GV ngang hàng cho MỌI lớp (không theo
    // PCCM, xem timetable.js buildSchedulerInput) — không có khái niệm "GV
    // chính" nên KHÔNG BAO GIỜ tính dạy thay, dù classSubjects (PCCM gốc) chỉ
    // ghi đúng 1 GV cố định (frontend không biết pool này, chỉ đọc PCCM gốc).
    // Nội dung 1 ô toàn trường.
    const cellText = (classId, cls, day, si, g) => {
      const vh = vhAt(classId, day, g);
      if (vh && vh[0] !== "HN") {
        const [sid, tid] = vh;
        const sn = subjectName[sid] || sid;
        const tc = isTc(vh) ? " (TC)" : "";
        return {
          text: (tid ? `${subjAbbr(sn)}-${lastWord(teacherName[tid] || "")}` : sn) + tc,
          kind: "vh",
        };
      }
      const n = ngheAt(cls.name, day, si, vh);
      if (n) {
        if (withNgheDetail) return { text: (n.subject ? `HN: ${n.subject}` : "Học nghề"), sub: [n.teacher, n.room].filter(Boolean).join(" • "), kind: "nghe" };
        return { text: "Học nghề", kind: "nghe" };
      }
      return null;
    };
    const exportExcel = () => {
      const header1 = ["Ngày", "Buổi", "Tiết", "Giờ", ...cols.map((c) => c.name)];
      const header2 = ["", "", "", "Phòng học VH", ...cols.map((c) => c.room || "")];
      const aoa = [header1, header2];
      DAYS.forEach((dLabel, day) => {
        sessions.forEach((ses, si) => {
          ses.periods.forEach((p, ti) => {
            aoa.push([
              si === 0 && ti === 0 ? `${day + 2} (ngày ${dayDM(startDate, day)})` : "",
              ti === 0 ? (ses.key || (si === 0 ? "S" : "C")) : "",
              ti + 1,
              p.time,
              ...cols.map((c) => {
                const cell = cellText(c.id, c, day, si, p.global);
                return cell ? cell.text + (cell.sub ? ` (${cell.sub})` : "") : "";
              }),
            ]);
          });
        });
      });
      downloadAoa(aoa, "TKB Toàn trường", `tkb_toan_truong_${withNgheDetail ? "vh_nghe" : "vh"}.xlsx`);
    };
    // Bản xuất riêng: CHỈ tên môn học, KHÔNG kèm tên GV (khác bản trên luôn
    // ghép "Mã môn-Tên GV") — dùng khi chỉ cần xem môn gì, không cần biết ai dạy.
    // sessionIndex: 0 = Sáng, 1 = Chiều — mỗi buổi xuất riêng 1 file. Mẫu
    // đúng theo bảng mẫu người dùng đưa: chỉ 2 cột đầu "Ngày"/"Tiết" (không
    // "Buổi"/"Giờ"/dòng phòng học), cột "Ngày" ghi "Thứ 2"... gộp Ô (merge)
    // suốt các tiết trong ngày đó. Ô "Nghỉ" (lớp nghỉ hẳn hôm đó) để TRỐNG,
    // không ghi chữ "Nghỉ".
    const exportExcelSubjectOnly = (sessionIndex) => {
      const sesLabel = sessionIndex === 0 ? "Sáng" : "Chiều";
      const header = ["Ngày", "Tiết", ...cols.map((c) => c.name)];
      const aoa = [header];
      const merges = [];
      DAYS.forEach((dLabel, day) => {
        sessions.forEach((ses, si) => {
          if (si !== sessionIndex) return;
          const startRow = aoa.length;
          ses.periods.forEach((p, ti) => {
            aoa.push([
              ti === 0 ? dLabel : "",
              ti + 1,
              ...cols.map((c) => {
                const vh = vhAt(c.id, day, p.global);
                if (vh && vh[0] !== "Nghỉ" && vh[0] !== "HN") return subjectName[vh[0]] || vh[0];
                const n = ngheAt(c.name, day, si, vh);
                if (n) return "Học nghề";
                return "";
              }),
            ]);
          });
          const endRow = aoa.length - 1;
          if (endRow > startRow) merges.push({ s: { r: startRow, c: 0 }, e: { r: endRow, c: 0 } });
        });
      });
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      if (merges.length) ws["!merges"] = merges;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName(`Chi mon - ${sesLabel}`));
      downloadWorkbook(wb, `tkb_toan_truong_${withNgheDetail ? "vh_nghe" : "vh"}_chi_mon_${sessionIndex === 0 ? "sang" : "chieu"}.xlsx`);
    };
    // Chọn ở combobox chỉ SỬA TẠI CHỖ (chưa gọi API) — phải bấm "Lưu" mới
    // ghi vào nháp (chưa duyệt), bấm "Duyệt" mới áp dụng thành version mới.
    // subjectId: bắt buộc khi ô đang TRỐNG (chọn cả môn lẫn GV); ô đã có môn
    // thì truyền lại đúng subjectId cũ (giữ nguyên môn, chỉ đổi GV).
    const pickCell = (classId, day, global, subjectId, teacherId) => {
      const key = `${classId}|${day}|${global}`;
      setPendingEdits((m) => ({ ...m, [key]: { subjectId, teacherId: teacherId || null } }));
    };
    const pendingCount = Object.keys(pendingEdits).length;
    const draftCount = Object.keys(draft).length;
    const saveDraft = async () => {
      setSaving(true);
      try {
        for (const key of Object.keys(pendingEdits)) {
          const [classId, dayStr, periodStr] = key.split("|");
          const edit = pendingEdits[key];
          await updateTimetableRunCell(schoolId, runId, {
            classId, day: Number(dayStr), period: Number(periodStr), teacherId: edit.teacherId, subjectId: edit.subjectId,
          });
        }
        const dr = await getTimetableRunDraft(schoolId, runId);
        setDraft(dr || {});
        setPendingEdits({});
        setError("");
      } catch (e) {
        window.alert(e.message || "Không lưu được — kiểm tra lại các ô vừa sửa.");
      } finally {
        setSaving(false);
      }
    };
    const approveDraft = async () => {
      if (!window.confirm(`Duyệt ${draftCount} ô đã sửa thành 1 bản (version) TKB mới? Bản hiện tại vẫn được giữ nguyên.`)) return;
      setApproving(true);
      try {
        const { newRunId } = await approveTimetableRunDraft(schoolId, runId);
        window.alert(`Đã duyệt — tạo bản mới (runId ${newRunId}). Vào lại danh sách bản xếp để chọn bản mới nhất.`);
        setDraft({});
        setEditMode(false);
      } catch (e) {
        window.alert(e.message || "Không duyệt được.");
      } finally {
        setApproving(false);
      }
    };
    // Bỏ các ô đang sửa tại chỗ, CHƯA bấm Lưu — không gọi API, chỉ xoá cục bộ.
    const discardPending = () => setPendingEdits({});
    // Bỏ hẳn bản nháp ĐÃ Lưu (chưa Duyệt) — gọi API xoá, quay lại đúng bản gốc.
    const discardDraft = async () => {
      if (!window.confirm(`Bỏ qua ${draftCount} ô đã lưu (chưa duyệt)? Sẽ quay lại đúng như bản gốc.`)) return;
      setSaving(true);
      try {
        await discardTimetableRunDraft(schoolId, runId);
        setDraft({});
      } catch (e) {
        window.alert(e.message || "Không bỏ được bản nháp.");
      } finally {
        setSaving(false);
      }
    };
    // Bước RIÊNG, chủ động bấm mới chạy (KHÔNG tự động lúc xếp) — sau khi
    // thuật toán đã xếp xong: với mỗi lớp-môn còn thiếu tiết + còn ô trống,
    // tìm 1 GV khác cùng môn cùng khối dạy thay. Ghi vào nháp, phải Duyệt mới
    // áp dụng — luôn báo rõ những lớp-môn phải dùng từ 2 GV dạy thay khác
    // nhau trở lên để người dùng tự cân nhắc trước khi Duyệt.
    const runKhoiSubstitute = async () => {
      if (!window.confirm("Tìm GV cùng môn, cùng khối dạy thay cho các lớp còn thiếu tiết + còn ô trống? Kết quả sẽ ghi vào bản nháp, chưa áp dụng ngay.")) return;
      setKhoiSubBusy(true);
      try {
        const result = await fillKhoiSubstitute(schoolId, runId);
        const dr = await getTimetableRunDraft(schoolId, runId);
        setDraft(dr || {});
        const chaos = (result.details || []).filter((d) => d.substitutes >= 2);
        let msg = `Đã lấp ${result.totalFilled}/${result.totalShortfall} tiết thiếu vào bản nháp (còn thiếu ${result.remaining}).`;
        if (chaos.length) {
          msg += `\n\n⚠ ${chaos.length} lớp-môn phải dùng từ 2 GV dạy thay khác nhau trở lên:\n`;
          msg += chaos.map((d) => `- ${d.className} ${d.subjectName}: ${d.substitutes} GV`).join("\n");
        }
        window.alert(msg);
      } catch (e) {
        window.alert(e.message || "Không chạy được.");
      } finally {
        setKhoiSubBusy(false);
      }
    };
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={runKhoiSubstitute} disabled={khoiSubBusy} style={editBtn}>
            {khoiSubBusy ? "Đang tìm..." : "🔁 Tìm GV dạy thay (cùng khối)"}
          </button>
          {editMode && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              Chọn GV cho ô môn học — <span style={{ color: "#059669" }}>■ rảnh</span> / <span style={{ color: "#dc2626" }}>■ đang bận</span>
            </span>
          )}
          {editMode && draftCount > 0 && (
            <span style={{ fontSize: 12, color: "#b45309" }}>⚠ Có {draftCount} ô đã lưu, chưa duyệt</span>
          )}
          <button type="button" onClick={() => setEditMode((v) => !v)} style={editMode ? editBtnActive : editBtn}>
            {editMode ? "✅ Xong sửa" : "✏️ Sửa GV"}
          </button>
          {editMode && pendingCount > 0 && (
            <button type="button" onClick={discardPending} style={editBtn}>↺ Bỏ sửa ({pendingCount})</button>
          )}
          {editMode && (
            <button type="button" onClick={saveDraft} disabled={saving || pendingCount === 0} style={pendingCount ? editBtnActive : editBtn}>
              {saving ? "Đang lưu..." : `💾 Lưu (${pendingCount})`}
            </button>
          )}
          {editMode && draftCount > 0 && (
            <button type="button" onClick={discardDraft} disabled={saving} style={{ ...editBtn, color: "#dc2626", borderColor: "#fca5a5" }}>
              🗑 Bỏ qua nháp ({draftCount})
            </button>
          )}
          {editMode && draftCount > 0 && (
            <button type="button" onClick={approveDraft} disabled={approving} style={{ ...editBtnActive, background: "#0ea5a3" }}>
              {approving ? "Đang duyệt..." : `✔ Duyệt (${draftCount})`}
            </button>
          )}
          <button type="button" onClick={exportExcel} style={exportBtn}>⬇ Xuất Excel</button>
          <button type="button" onClick={() => exportExcelSubjectOnly(0)} style={exportBtn}>⬇ Xuất Excel (chỉ môn, không GV) Sáng</button>
          <button type="button" onClick={() => exportExcelSubjectOnly(1)} style={exportBtn}>⬇ Xuất Excel (chỉ môn, không GV) Chiều</button>
        </div>
        <div style={{ overflow: "auto", maxHeight: "78vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ ...thFix(0), zIndex: 6 }}>Ngày</th>
              <th style={{ ...thFix(38), zIndex: 6 }}>Buổi</th>
              <th style={{ ...thFix(74), zIndex: 6 }}>Tiết</th>
              <th style={{ ...thFix(108), zIndex: 6, minWidth: 92 }}>Giờ</th>
              {cols.map((c) => (<th key={c.id} style={{ ...th, top: 0, position: "sticky", zIndex: 3, minWidth: 78 }}>{c.name}</th>))}
            </tr>
            <tr>
              <th colSpan={3} style={{ ...thFix(0), top: 22, zIndex: 5 }}></th>
              <th style={{ ...thFix(108), top: 22, zIndex: 5, fontWeight: 400, color: "#6b7280" }}>Phòng học VH</th>
              {cols.map((c) => (<th key={c.id} style={{ ...th, top: 22, position: "sticky", zIndex: 3, fontWeight: 400, color: "#6b7280" }}>{c.room || ""}</th>))}
            </tr>
          </thead>
          <tbody>
            {DAYS.map((dLabel, day) =>
              sessions.map((ses, si) =>
                ses.periods.map((p, ti) => (
                  <tr key={`${day}-${si}-${ti}`} data-pi={periodShadeIndex(p.global)}>
                    {si === 0 && ti === 0 && (
                      <td rowSpan={periodsPerDay} style={{ ...tdName, position: "sticky", left: 0, zIndex: 2, textAlign: "center", background: "#f9fafb", verticalAlign: "middle" }}>
                        <div style={{ fontWeight: 700 }}>{day + 2}</div>
                        <div style={{ color: "#6b7280", fontSize: 10 }}>ngày {dayDM(startDate, day)}</div>
                      </td>
                    )}
                    {ti === 0 && (
                      <td rowSpan={ses.periods.length} style={{ ...td, position: "sticky", left: 38, zIndex: 2, fontWeight: 600, background: "#f9fafb", verticalAlign: "middle" }}>{ses.key || (si === 0 ? "S" : "C")}</td>
                    )}
                    <td style={{ ...td, position: "sticky", left: 74, zIndex: 2, background: "#fff", fontWeight: 600 }}>{ti + 1}</td>
                    <td style={{ ...td, position: "sticky", left: 108, zIndex: 2, background: "#fff", whiteSpace: "nowrap", color: "#6b7280" }}>{p.time}</td>
                    {cols.map((c) => {
                      const cell = cellText(c.id, c, day, si, p.global);
                      const key = `${c.id}|${day}|${p.global}`;
                      if (editMode && cell && cell.kind === "vh") {
                        const vh = vhAt(c.id, day, p.global);
                        const [sid, tid] = vh;
                        const candidates = teacherCandidatesFor(c.id, sid);
                        // Giá trị hiện trên combobox: đang sửa tại chỗ (chưa Lưu) > đã lưu vào nháp (chưa Duyệt) > GV gốc.
                        const edit = pendingEdits[key];
                        const effTid = edit !== undefined ? edit.teacherId : (draft[key] ? draft[key][1] : tid);
                        const isDirty = edit !== undefined;
                        const isDrafted = !isDirty && draft[key] !== undefined;
                        return (
                          <td key={c.id} style={{ ...td, background: isDirty ? "#fef9c3" : isDrafted ? "#fef3c7" : "#eff6ff", padding: 2 }}>
                            <select
                              value={effTid || ""}
                              onChange={(e) => pickCell(c.id, day, p.global, sid, e.target.value)}
                              style={{ width: "100%", fontSize: 10, padding: 2, border: "1px solid #93c5fd", borderRadius: 4 }}
                            >
                              <option value="">— chọn GV —</option>
                              {candidates.map((cand) => {
                                const busy = isTeacherBusyAt(cand, day, p.global);
                                return (
                                  <option key={cand} value={cand} style={{ color: busy ? "#dc2626" : "#059669" }}>
                                    {teacherName[cand] || cand}{busy ? " (bận)" : ""}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                        );
                      }
                      // Ô đang TRỐNG (chưa có môn nào cả) — cho chọn cả môn lẫn GV.
                      if (editMode && !cell) {
                        const edit = pendingEdits[key];
                        const draftHere = draft[key];
                        const effSid = edit ? edit.subjectId : (draftHere ? draftHere[0] : "");
                        const effTid = edit ? edit.teacherId : (draftHere ? draftHere[1] : "");
                        const isDirty = edit !== undefined;
                        const isDrafted = !isDirty && draftHere !== undefined;
                        const candidates = effSid ? teacherCandidatesFor(c.id, effSid) : [];
                        return (
                          <td key={c.id} style={{ ...td, background: isDirty ? "#fef9c3" : isDrafted ? "#fef3c7" : "#f9fafb", padding: 2 }}>
                            <select
                              value={effSid || ""}
                              onChange={(e) => pickCell(c.id, day, p.global, e.target.value, "")}
                              style={{ width: "100%", fontSize: 9, padding: 1, border: "1px solid #d1d5db", borderRadius: 4, marginBottom: 1 }}
                            >
                              <option value="">— chọn môn —</option>
                              {(c.subjects || []).map((s) => (
                                <option key={s.subjectId} value={s.subjectId}>{subjectName[s.subjectId] || s.subjectId}</option>
                              ))}
                            </select>
                            {effSid && (
                              <select
                                value={effTid || ""}
                                onChange={(e) => pickCell(c.id, day, p.global, effSid, e.target.value)}
                                style={{ width: "100%", fontSize: 9, padding: 1, border: "1px solid #93c5fd", borderRadius: 4 }}
                              >
                                <option value="">— chọn GV —</option>
                                {candidates.map((cand) => {
                                  const busy = isTeacherBusyAt(cand, day, p.global);
                                  return (
                                    <option key={cand} value={cand} style={{ color: busy ? "#dc2626" : "#059669" }}>
                                      {teacherName[cand] || cand}{busy ? " (bận)" : ""}
                                    </option>
                                  );
                                })}
                              </select>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td
                          key={c.id}
                          style={{ ...td, background: cell ? (cell.kind === "nghe" ? "#fef3c7" : "#eff6ff") : "#fff" }}
                          title={cell ? `${cell.text} ${cell.sub || ""}` : ""}
                        >
                          {cell ? (<><div>{cell.text}</div>{cell.sub && <div style={{ color: "#6b7280", fontSize: 9 }}>{cell.sub}</div>}</>) : ""}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )
            )}
          </tbody>
        </table>
        </div>
      </div>
    );
  }

  // ================= TKB GV TOÀN TRƯỜNG THEO BUỔI (Sáng / Chiều) =================
  if (mode === "teacherGridSang" || mode === "teacherGridChieu") {
    const si = mode === "teacherGridSang" ? 0 : 1;
    const ses = sessions[si];
    if (!ses) return <p style={{ color: "#6b7280" }}>Chưa cấu hình giờ học (mục "Thời gian học").</p>;
    const label = mode === "teacherGridSang" ? "Buổi Sáng" : "Buổi Chiều";

    // teacherId -> "day_period" -> tên lớp
    const cellOf = {};
    const sessionGlobals = new Set(ses.periods.map((p) => p.global));
    const teacherIdsInSession = new Set();
    Object.entries(grid).forEach(([key, val]) => {
      if (!val || !val[1]) return;
      const [classId, d, p] = key.split("|");
      (cellOf[val[1]] = cellOf[val[1]] || {})[`${d}_${p}`] = (classById[classId] || {}).name || classId;
      if (sessionGlobals.has(Number(p))) teacherIdsInSession.add(val[1]);
    });
    // Chỉ lấy GV có ít nhất 1 tiết trong đúng buổi (Sáng/Chiều) đang xem.
    const teachersInSession = teachers.filter((t) => teacherIdsInSession.has(t.id));

    const exportExcel = () => {
      const header1 = ["Giáo viên", ...DAYS.flatMap((d) => Array(ses.periods.length).fill(d))];
      const header2 = ["", ...DAYS.flatMap(() => ses.periods.map((_, i) => `Tiết ${i + 1}`))];
      const aoa = [header1, header2];
      teachersInSession.forEach((t) => {
        const row = [t.name];
        DAYS.forEach((_, day) => ses.periods.forEach((p) => row.push((cellOf[t.id] || {})[`${day}_${p.global}`] || "")));
        aoa.push(row);
      });
      downloadAoa(aoa, `TKB GV ${label}`, `tkb_gv_${mode === "teacherGridSang" ? "sang" : "chieu"}.xlsx`);
    };

    const dayBorder = { borderLeft: "2px solid #9ca3af" };

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
          <strong style={{ fontSize: 15 }}>🕐 TKB Giáo viên toàn trường — {label}</strong>
          <button type="button" onClick={exportExcel} style={exportBtn}>⬇ Xuất Excel</button>
        </div>
        <div style={{ overflow: "auto", maxHeight: "78vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ ...thFix(0), zIndex: 6, minWidth: 150 }}>Giáo viên</th>
                {DAYS.map((d, di) => (<th key={d} colSpan={ses.periods.length} style={{ ...th, top: 0, position: "sticky", zIndex: 3, ...(di > 0 ? dayBorder : {}) }}>{d}</th>))}
              </tr>
              <tr>
                <th style={{ ...thFix(0), top: 22, zIndex: 5 }}></th>
                {DAYS.map((d, di) => ses.periods.map((_, i) => (
                  <th key={d + i} style={{ ...th, top: 22, position: "sticky", zIndex: 3, fontWeight: 400, color: "#6b7280", minWidth: 54, ...(di > 0 && i === 0 ? dayBorder : {}) }}>{i + 1}</th>
                )))}
              </tr>
            </thead>
            <tbody>
              {teachersInSession.map((t) => (
                <tr key={t.id}>
                  <td style={{ ...thFix(0), textAlign: "left", fontWeight: 600, background: "#fff" }}>{t.name}</td>
                  {DAYS.map((_, day) => ses.periods.map((p, i) => {
                    const val = (cellOf[t.id] || {})[`${day}_${p.global}`];
                    return (
                      <td key={`${day}_${p.global}`} style={{ ...td, background: val ? "#eff6ff" : "#fff", ...(day > 0 && i === 0 ? dayBorder : {}) }}>
                        {val || ""}
                      </td>
                    );
                  }))}
                </tr>
              ))}
              {teachersInSession.length === 0 && (
                <tr><td colSpan={1 + DAYS.length * ses.periods.length} style={{ ...td, color: "#9ca3af" }}>Chưa có giáo viên nào có tiết trong buổi này.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ================= TKB GV-NGHỀ TOÀN TRƯỜNG THEO BUỔI (Sáng / Chiều) =================
  // Nguồn dữ liệu: TKB dạy nghề (voc.rows), không phải grid văn hoá. Tên GV
  // trong lịch nghề chỉ là chuỗi tự do "Thầy X"/"Cô Y" -> hiển thị bỏ kính ngữ
  // (chỉ giữ Họ, tên đệm, tên). Nghề chỉ có tiết 1-4/buổi, tiết 5 luôn trống.
  if (mode === "teacherGridNgheSang" || mode === "teacherGridNgheChieu") {
    const si = mode === "teacherGridNgheSang" ? 0 : 1;
    const ses = sessions[si];
    if (!ses) return <p style={{ color: "#6b7280" }}>Chưa cấu hình giờ học (mục "Thời gian học").</p>;
    const label = mode === "teacherGridNgheSang" ? "Buổi Sáng" : "Buổi Chiều";

    const nameSet = new Map(); // normName -> tên hiển thị (đã bỏ kính ngữ)
    (voc.rows || []).forEach((r) => {
      if (!r.teacher) return;
      const disp = stripHonorific(r.teacher);
      if (disp) nameSet.set(norm(disp), disp);
    });
    const ngheTeachers = Array.from(nameSet.entries())
      .filter(([key]) => [0, 1, 2, 3, 4, 5].some((d) => ngheByTeacherName[`${key}|${d}|${si}`]))
      .map(([key, disp]) => ({ key, disp }))
      .sort((a, b) => a.disp.localeCompare(b.disp, "vi"));

    const cellOf = (key, day, periodIdx) => {
      if (periodIdx > NGHE_MAX_IDX) return "";
      const hits = ngheByTeacherName[`${key}|${day}|${si}`];
      return hits && hits.length ? hits.map((h) => h.className).join(", ") : "";
    };

    const exportExcel = () => {
      const header1 = ["Giáo viên", ...DAYS.flatMap((d) => Array(ses.periods.length).fill(d))];
      const header2 = ["", ...DAYS.flatMap(() => ses.periods.map((_, i) => `Tiết ${i + 1}`))];
      const aoa = [header1, header2];
      ngheTeachers.forEach((t) => {
        const row = [t.disp];
        DAYS.forEach((_, day) => ses.periods.forEach((p, i) => row.push(cellOf(t.key, day, i))));
        aoa.push(row);
      });
      downloadAoa(aoa, `TKB GV-Nghề ${label}`, `tkb_gv_nghe_${mode === "teacherGridNgheSang" ? "sang" : "chieu"}.xlsx`);
    };

    const dayBorder = { borderLeft: "2px solid #9ca3af" };

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
          <strong style={{ fontSize: 15 }}>🛠️ TKB Giáo viên dạy nghề toàn trường — {label}</strong>
          <button type="button" onClick={exportExcel} style={exportBtn}>⬇ Xuất Excel</button>
        </div>
        <div style={{ overflow: "auto", maxHeight: "78vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ ...thFix(0), zIndex: 6, minWidth: 150 }}>Giáo viên</th>
                {DAYS.map((d, di) => (<th key={d} colSpan={ses.periods.length} style={{ ...th, top: 0, position: "sticky", zIndex: 3, ...(di > 0 ? dayBorder : {}) }}>{d}</th>))}
              </tr>
              <tr>
                <th style={{ ...thFix(0), top: 22, zIndex: 5 }}></th>
                {DAYS.map((d, di) => ses.periods.map((_, i) => (
                  <th key={d + i} style={{ ...th, top: 22, position: "sticky", zIndex: 3, fontWeight: 400, color: "#6b7280", minWidth: 54, ...(di > 0 && i === 0 ? dayBorder : {}) }}>{i + 1}</th>
                )))}
              </tr>
            </thead>
            <tbody>
              {ngheTeachers.map((t) => (
                <tr key={t.key}>
                  <td style={{ ...thFix(0), textAlign: "left", fontWeight: 600, background: "#fff" }}>{t.disp}</td>
                  {DAYS.map((_, day) => ses.periods.map((p, i) => {
                    const val = cellOf(t.key, day, i);
                    return (
                      <td key={`${day}_${p.global}`} style={{ ...td, background: val ? "#fef3c7" : "#fff", ...(day > 0 && i === 0 ? dayBorder : {}) }}>
                        {val || ""}
                      </td>
                    );
                  }))}
                </tr>
              ))}
              {ngheTeachers.length === 0 && (
                <tr><td colSpan={1 + DAYS.length * ses.periods.length} style={{ ...td, color: "#9ca3af" }}>Chưa có giáo viên dạy nghề nào trong buổi này.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ================= CHẤM CÔNG GV VĂN HOÁ =================
  if (mode === "chamcong") {
    // Mẫu MỚI (CH=0đ, TG tách tiết thường/TC 2 đơn giá) CHỈ áp dụng từ tuần
    // bắt đầu 07/09/2026 trở đi — các tuần TRƯỚC vẫn giữ NGUYÊN mẫu cũ (đơn
    // giá cố định 80.000đ/tiết cho MỌI GV, không tách TC) để không tính lại
    // sai lương những tháng đã chấm công rồi.
    const useNewPayroll = String(startDate || "") >= "2026-09-07";
    const UNIT_PRICE_OLD = 80000;
    // Sinh hoạt (HĐTN(SH)) không tính vào chấm công Văn hoá — GVCN đứng lớp
    // Sinh hoạt không phải "dạy" theo nghĩa chấm công tiết văn hoá.
    const CHAMCONG_EXCLUDED_SUBJECT_NAMES = new Set(["HĐTN(SH)"]);
    const isChamCongExcludedSubject = (sid) => CHAMCONG_EXCLUDED_SUBJECT_NAMES.has(subjectName[sid]);
    // Level GV Thỉnh giảng (hồ sơ tgLevel): từ 01/09/2026, Level 1 chỉ được
    // tính tiết TC bằng 80.000đ, Level 2 (dạy lớp 12 môn thi TN) giữ 130.000đ.
    // Xét theo NGÀY THẬT của tiết (tuần bắc 2 tháng tính đúng từng ngày).
    const isoOfDay = (day) => {
      const d = new Date(`${startDate}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + Number(day || 0));
      return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
    };
    const teacherById = Object.fromEntries(teachers.map((t) => [t.id, t]));
    const tcPriceOf = (tid, day) =>
      isoOfDay(day) >= TG_LEVEL_FROM && Number((teacherById[tid] || {}).tgLevel) !== 2 ? UNIT_PRICE_TC_L1 : UNIT_PRICE_TC;
    const tally = {}; // teacherId -> { tietThuong, tietTC, tienTC, subj: {subjectName: count} }
    Object.keys(grid).forEach((key) => {
      const val = grid[key];
      if (!Array.isArray(val)) return;
      const [sid, tid, tc] = val;
      if (!tid || isChamCongExcludedSubject(sid)) return;
      const t = (tally[tid] = tally[tid] || { tietThuong: 0, tietTC: 0, tienTC: 0, subj: {} });
      if (tc === true) { t.tietTC += 1; t.tienTC += tcPriceOf(tid, key.split("|")[1]); } else t.tietThuong += 1;
      const sn = subjectName[sid] || sid;
      t.subj[sn] = (t.subj[sn] || 0) + 1;
    });
    // GV Cơ hữu: đơn giá 0đ (ăn lương cố định) — vẫn hiện số tiết để theo dõi
    // khối lượng, chỉ riêng THÀNH TIỀN = 0. GV Thỉnh giảng: tiết thường
    // 80.000đ, tiết Tăng cường (TC) 130.000đ — tách riêng 2 khoản.
    const rows = teachers
      .filter((t) => tally[t.id])
      .filter((t) => !lockTeacher || t.id === defaultTeacherId) // GV tự xem -> chỉ thấy công của chính mình
      .map((t) => {
        const info = tally[t.id];
        const bomon = Object.entries(info.subj).sort((a, b) => b[1] - a[1])[0][0];
        const loai = String(t.employmentType || "").normalize("NFC") === "Thỉnh giảng" ? "TG" : "CH";
        const tiet = info.tietThuong + info.tietTC;
        if (!useNewPayroll) {
          // Mẫu cũ: đơn giá cố định cho mọi loại GV, không tách TC.
          return { name: t.name, bomon, loai, tietThuong: tiet, tietTC: 0, tiet, tienThuong: tiet * UNIT_PRICE_OLD, tienTC: 0, tien: tiet * UNIT_PRICE_OLD };
        }
        const isTG = loai === "TG";
        const tienThuong = isTG ? info.tietThuong * UNIT_PRICE_THUONG : 0;
        const tienTC = isTG ? info.tienTC : 0;
        return {
          name: t.name, bomon, loai,
          tietThuong: info.tietThuong, tietTC: info.tietTC, tiet,
          tienThuong, tienTC, tien: tienThuong + tienTC,
        };
      })
      .sort((a, b) => a.loai.localeCompare(b.loai) || a.bomon.localeCompare(b.bomon, "vi") || a.name.localeCompare(b.name, "vi"));
    const sumTietThuong = rows.reduce((s, r) => s + r.tietThuong, 0);
    const sumTietTC = rows.reduce((s, r) => s + r.tietTC, 0);
    const sumTienThuong = rows.reduce((s, r) => s + r.tienThuong, 0);
    const sumTienTC = rows.reduce((s, r) => s + r.tienTC, 0);
    const sumTien = rows.reduce((s, r) => s + r.tien, 0);
    if (rows.length === 0) return <p style={{ color: "#6b7280" }}>Bản này chưa có tiết văn hoá nào để chấm công.</p>;

    // Mỗi buổi (Sáng/Chiều) x mỗi ngày có tiết -> 1 nhóm riêng, dùng chung cho cả
    // hiển thị trên màn hình (dạng tab) lẫn xuất Excel (dạng sheet).
    const perSlot = {}; // "teacherId|classId|subjectId|day|si" -> { subjectId, countThuong, countTC }
    Object.entries(grid).forEach(([key, val]) => {
      if (!val || !val[1] || isChamCongExcludedSubject(val[0])) return;
      if (lockTeacher && val[1] !== defaultTeacherId) return;
      const [classId, d, p] = key.split("|");
      const day = Number(d);
      const si = sessionOfGlobal(Number(p));
      if (si < 0) return;
      // Khoá gộp phải có subjectId — cùng 1 GV dạy CÙNG 1 lớp nhưng 2 MÔN khác
      // nhau trong cùng buổi (vd Văn + Sinh hoạt) không được gộp lẫn vào nhau.
      const k = `${val[1]}|${classId}|${val[0]}|${day}|${si}`;
      const e = (perSlot[k] = perSlot[k] || { teacherId: val[1], classId, day, si, subjectId: val[0], countThuong: 0, countTC: 0 });
      if (val[2] === true) e.countTC += 1; else e.countThuong += 1;
    });
    // Mẫu MỚI: mỗi (GV, Loại tiết) là 1 dòng — CK (chính khoá, mặc định) và TC
    // (tăng cường) tách dòng riêng, mỗi dòng có Đơn giá + Thành tiền riêng
    // (Thành tiền = Đơn giá × Số tiết). Cùng 1 GV dạy nhiều lớp cùng loại tiết
    // trong buổi thì GỘP thành 1 dòng — Số tiết cộng dồn, cột Lớp nối các lớp
    // bằng dấu phẩy — để khớp đúng mẫu kế toán.
    const rowsForSlot = (day, si) => {
      const groups = Object.values(perSlot).filter((e) => e.day === day && e.si === si);
      const merged = {}; // "teacherId|loaiTiet|bomon" -> { ...gộp nhiều lớp CÙNG môn }
      groups.forEach((e) => {
        const t = teachers.find((tt) => tt.id === e.teacherId);
        const loai = t && String(t.employmentType || "").normalize("NFC") === "Thỉnh giảng" ? "TG" : "CH";
        const name = t ? t.name : e.teacherId;
        const bomon = subjectName[e.subjectId] || "";
        const lop = (classById[e.classId] || {}).name || e.classId;
        const addLine = (soTiet, loaiTiet, donGia) => {
          if (soTiet <= 0) return;
          // Gộp theo GV + loại tiết + MÔN — khác môn (vd Văn với HĐTN(SH))
          // không được gộp chung 1 dòng, để không "nuốt" mất tiết môn ít hơn.
          const mergeKey = `${e.teacherId}|${loaiTiet}|${bomon}`;
          const g = (merged[mergeKey] = merged[mergeKey] || { name, loai, loaiTiet, donGia, bomon, soTiet: 0, thanhTien: 0, lops: [] });
          g.soTiet += soTiet;
          g.thanhTien += donGia * soTiet;
          if (!g.lops.includes(lop)) g.lops.push(lop);
        };
        if (!useNewPayroll) {
          addLine(e.countThuong + e.countTC, "CK", UNIT_PRICE_OLD);
          return;
        }
        const isTG = loai === "TG";
        addLine(e.countThuong, "CK", isTG ? UNIT_PRICE_THUONG : 0);
        addLine(e.countTC, "TC", isTG ? tcPriceOf(e.teacherId, e.day) : 0);
      });
      return Object.values(merged)
        .map((g) => ({
          name: g.name, loai: g.loai, loaiTiet: g.loaiTiet, donGia: g.donGia, bomon: g.bomon,
          soTiet: g.soTiet, thanhTien: g.thanhTien, tiet: g.soTiet, tien: g.thanhTien,
          lop: g.lops.join(", "),
        }))
        .sort((a, b) => a.loai.localeCompare(b.loai) || a.name.localeCompare(b.name, "vi") || a.bomon.localeCompare(b.bomon, "vi"));
    };
    // Danh sách buổi thực sự có tiết, theo đúng thứ tự Sáng T2, Chiều T2, Sáng T3, Chiều T3...
    const buoiList = [];
    DAYS.forEach((dLabel, day) => {
      sessions.forEach((ses, si) => {
        if (rowsForSlot(day, si).length === 0) return;
        const label = `${ses.label || (si === 0 ? "Sáng" : "Chiều")} ${dLabel} (${dayDM(startDate, day)})`;
        buoiList.push({ key: `${day}_${si}`, day, si, label });
      });
    });

    const sumTiet = sumTietThuong + sumTietTC;
    const headerNote = useNewPayroll
      ? (isoOfDay(5) >= TG_LEVEL_FROM
        ? `TG: ${UNIT_PRICE_THUONG.toLocaleString("vi-VN")}đ/tiết thường, tiết TC Level 2: ${UNIT_PRICE_TC.toLocaleString("vi-VN")}đ, Level 1: ${UNIT_PRICE_TC_L1.toLocaleString("vi-VN")}đ — CH: 0đ`
        : `TG: ${UNIT_PRICE_THUONG.toLocaleString("vi-VN")}đ/tiết thường, ${UNIT_PRICE_TC.toLocaleString("vi-VN")}đ/tiết TC — CH: 0đ`)
      : `đơn giá ${UNIT_PRICE_OLD.toLocaleString("vi-VN")}đ/tiết`;
    // Thông tin bản công bố ghi kèm khi in / xuất Excel: "Bản 12 · công bố 15:25 02/11/2026 (chính thức) bởi …".
    const fmtDT = (t) => { const d = new Date(Number(t) + 7 * 3600 * 1000).toISOString(); return `${d.slice(11, 16)} ${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`; };
    const banInfo = [
      banNo || "Bản TKB",
      run && run.published
        ? `công bố ${run.publishedAt ? fmtDT(run.publishedAt) : ""} (${run.publishMode === "demo" ? "DEMO — không tính chấm công/lương" : "chính thức"})${banBy ? ` bởi ${banBy}` : ""}`.replace("công bố  (", "công bố (")
        : "CHƯA công bố",
    ].join(" · ");

    const exportExcel = async () => {
      // Dùng ExcelJS (không phải "xlsx") để có font Times New Roman + kẻ ô
      // thật trong file xuất — thư viện "xlsx" bản community không style được.
      const wb = new ExcelJS.Workbook();

      const totalHeader = useNewPayroll
        ? ["STT", "Họ và tên", "Bộ môn", "Loại", "Tiết thường", "Tiết TC", "Tiền tiết thường", "Tiền tiết TC", "Tổng tiền"]
        : ["STT", "Họ và tên", "Bộ môn", "Loại", "Số tiết", "Đơn giá", "Thành tiền"];
      const totalAoa = [[`CHẤM CÔNG DẠY VĂN HOÁ — Tuần ${dayDM(startDate, 0)}–${dayDM(startDate, 5)} (${headerNote})`], [`Bản TKB: ${banInfo}`], totalHeader];
      rows.forEach((r, i) => totalAoa.push(
        useNewPayroll
          ? [i + 1, r.name, r.bomon, r.loai, r.tietThuong, r.tietTC, r.tienThuong, r.tienTC, r.tien]
          : [i + 1, r.name, r.bomon, r.loai, r.tiet, UNIT_PRICE_OLD, r.tien]
      ));
      totalAoa.push(
        useNewPayroll
          ? ["", "Tổng", "", "", sumTietThuong, sumTietTC, sumTienThuong, sumTienTC, sumTien]
          : ["", "Tổng", "", "", sumTiet, "", sumTien]
      );
      const wsTotal = wb.addWorksheet(safeSheetName("Tổng cả tuần"));
      totalAoa.forEach((row) => wsTotal.addRow(row));
      styleSheetTimesNewRomanBordered(wsTotal);

      buoiList.forEach(({ day, si, label }) => {
        const sheetRows = rowsForSlot(day, si);
        const wsBuoi = wb.addWorksheet(safeSheetName(label.replace("/", "-")));
        wsBuoi.addRow([`CHẤM CÔNG VĂN HOÁ — ${label}`]);
        wsBuoi.addRow([`Bản TKB: ${banInfo}`]);
        if (useNewPayroll) {
          wsBuoi.addRow(["STT", "Họ và tên", "Bộ môn", "Lớp", "Hình thức", "Số tiết", "Loại tiết", "Đơn giá", "Thành tiền", "Ký dạy"]);
          const dataRows = sheetRows.map((r, i) => {
            // Thành tiền để trống, gán công thức = Số tiết × Đơn giá ngay bên dưới
            // để khi sửa Đơn giá/Số tiết trong Excel, Thành tiền tự tính lại.
            const row = wsBuoi.addRow([i + 1, r.name, r.bomon, r.lop, r.loai, r.soTiet, r.loaiTiet, r.donGia, null, ""]);
            row.getCell(9).value = { formula: `F${row.number}*H${row.number}` };
            return row;
          });
          const totalRow = wsBuoi.addRow(["TỔNG", "", "", "", "", null, "", "", null, ""]);
          if (dataRows.length > 0) {
            const first = dataRows[0].number;
            const last = dataRows[dataRows.length - 1].number;
            totalRow.getCell(6).value = { formula: `SUM(F${first}:F${last})` };
            totalRow.getCell(9).value = { formula: `SUM(I${first}:I${last})` };
          } else {
            totalRow.getCell(6).value = 0;
            totalRow.getCell(9).value = 0;
          }
        } else {
          wsBuoi.addRow(["STT", "Họ và tên", "Bộ môn", "Lớp", "Số tiết", "Hình thức", "Đơn giá", "Thành tiền", "Ký dạy"]);
          sheetRows.forEach((r, i) => wsBuoi.addRow([i + 1, r.name, r.bomon, r.lop, r.tiet, r.loai, UNIT_PRICE_OLD, r.tien, ""]));
          const sumT = sheetRows.reduce((s, r) => s + r.tiet, 0);
          const sumTienBuoi = sheetRows.reduce((s, r) => s + r.tien, 0);
          wsBuoi.addRow(["TỔNG", "", "", "", sumT, "", "", sumTienBuoi, ""]);
        }
        styleSheetTimesNewRomanBordered(wsBuoi);
      });

      await downloadExcelJsWorkbook(wb, "cham_cong_gv_van_hoa.xlsx");
    };

    const activeBuoi = buoiList.find((b) => b.key === chamcongTab);
    const slotRows = activeBuoi ? rowsForSlot(activeBuoi.day, activeBuoi.si) : [];
    const slotSumSoTiet = slotRows.reduce((s, r) => s + r.soTiet, 0);
    const slotSumThanhTien = slotRows.reduce((s, r) => s + r.thanhTien, 0);
    const slotSumTiet = slotRows.reduce((s, r) => s + r.tiet, 0);
    const slotSumTien = slotRows.reduce((s, r) => s + r.tien, 0);

    // In: dựng 1 trang HTML riêng (Times New Roman, kẻ ô, khổ Tabloid 17×11in nằm ngang) trong iframe ẩn
    // rồi gọi print() — không in cả giao diện app. which="current" in đúng tab
    // đang xem; which="all" in Tổng cả tuần + mỗi buổi sang 1 trang mới.
    const printChamCong = (which) => {
      const esc = (v) => String(v == null ? "" : v).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const money = (n) => Number(n || 0).toLocaleString("vi-VN");
      // Tự chia trang: tối đa PRINT_ROWS_MAX dòng/trang (kể cả dòng Tổng) và
      // CHIA ĐỀU giữa các trang (vd 25 dòng -> 13 + 12, không phải 16 + 9);
      // chiều cao dòng giãn ra để mỗi trang lấp gần kín khổ giấy.
      const PRINT_ROWS_MAX = 16;
      const PRINT_ROWS_AREA_MM = 218; // phần cao dành cho các dòng dữ liệu (đã trừ tiêu đề + dòng header)
      const cellsHtml = (r) => r.map((c) => `<td${c && c.span ? ` colspan="${c.span}"` : ""}${c && c.cls ? ` class="${c.cls}"` : ""}>${esc(c && typeof c === "object" ? c.v : c)}</td>`).join("");
      const table = (head, body, foot, title) => {
        const all = [...body.map((r) => ({ r })), { r: foot, sum: true }];
        const nPages = Math.ceil(all.length / PRINT_ROWS_MAX);
        const perPage = Math.ceil(all.length / nPages);
        const rowH = Math.min(16, Math.floor((PRINT_ROWS_AREA_MM / perPage) * 10) / 10);
        const out = [];
        for (let pg = 0; pg < nPages; pg++) {
          const chunk = all.slice(pg * perPage, (pg + 1) * perPage);
          const h1 = nPages > 1 ? `${title} (trang ${pg + 1}/${nPages})` : title;
          out.push(`<section><h1>${esc(h1)}</h1><p class="note">${esc(headerNote)}</p><p class="note ban">${esc("Bản TKB: " + banInfo)}</p><table><thead><tr>${head.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead><tbody>${chunk
            .map((x) => `<tr${x.sum ? ' class="sum"' : ""} style="height:${rowH}mm">${cellsHtml(x.r)}</tr>`)
            .join("")}</tbody></table></section>`);
        }
        return out.join("");
      };
      const R = (v) => ({ v, cls: "r" });
      const L = (v) => ({ v, cls: "l" });
      const totalPage = () => {
        const title = `CHẤM CÔNG DẠY VĂN HOÁ — Tuần ${dayDM(startDate, 0)}–${dayDM(startDate, 5)}`;
        const t = useNewPayroll
          ? table(
              ["STT", "Họ và tên", "Bộ môn", "Loại", "Tiết thường", "Tiết TC", "Tiền tiết thường", "Tiền tiết TC", "Tổng tiền"],
              rows.map((r, i) => [i + 1, L(r.name), r.bomon, r.loai, r.tietThuong, r.tietTC, R(money(r.tienThuong)), R(money(r.tienTC)), R(money(r.tien))]),
              [{ v: "Tổng", span: 4, cls: "r" }, sumTietThuong, sumTietTC, R(money(sumTienThuong)), R(money(sumTienTC)), R(money(sumTien))],
              title
            )
          : table(
              ["STT", "Họ và tên", "Bộ môn", "Loại", "Số tiết", "Đơn giá", "Thành tiền"],
              rows.map((r, i) => [i + 1, L(r.name), r.bomon, r.loai, r.tiet, money(UNIT_PRICE_OLD), R(money(r.tien))]),
              [{ v: "Tổng", span: 4, cls: "r" }, sumTiet, "", R(money(sumTien))],
              title
            );
        return t;
      };
      const buoiPage = (b) => {
        const sr = rowsForSlot(b.day, b.si);
        const sSoTiet = sr.reduce((s, r) => s + r.soTiet, 0);
        const sTien = sr.reduce((s, r) => s + r.thanhTien, 0);
        const title = `CHẤM CÔNG VĂN HOÁ — ${b.label}`;
        const t = useNewPayroll
          ? table(
              ["STT", "Họ và tên", "Bộ môn", "Lớp", "Hình thức", "Số tiết", "Loại tiết", "Đơn giá", "Thành tiền", "Ký dạy"],
              sr.map((r, i) => [i + 1, L(r.name), r.bomon, r.lop, r.loai, r.soTiet, r.loaiTiet, money(r.donGia), R(money(r.thanhTien)), { v: "", cls: "sign" }]),
              [{ v: "TỔNG", span: 5, cls: "r" }, sSoTiet, "", "", R(money(sTien)), ""],
              title
            )
          : table(
              ["STT", "Họ và tên", "Bộ môn", "Lớp", "Số tiết", "Hình thức", "Đơn giá", "Thành tiền", "Ký dạy"],
              sr.map((r, i) => [i + 1, L(r.name), r.bomon, r.lop, r.tiet, r.loai, money(UNIT_PRICE_OLD), R(money(r.tien)), { v: "", cls: "sign" }]),
              [{ v: "TỔNG", span: 4, cls: "r" }, sSoTiet, "", "", R(money(sTien)), ""],
              title
            );
        return t;
      };
      const pages = which === "all"
        ? [totalPage(), ...buoiList.map(buoiPage)]
        : [activeBuoi && chamcongTab !== "total" ? buoiPage(activeBuoi) : totalPage()];
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Cham cong GV Van hoa</title><style>
        @page { size: 17in 11in; margin: 10mm 10mm; }
        body { font-family: 'Times New Roman', Times, serif; font-size: 16pt; color: #000; margin: 0; }
        section { page-break-after: always; break-after: page; }
        section:last-child { page-break-after: auto; break-after: auto; }
        h1 { font-size: 19pt; text-align: center; margin: 0 0 2px; }
        .note { text-align: center; font-style: italic; font-size: 13pt; margin: 0 0 4px; }
        .note.ban { font-style: normal; font-weight: 700; margin: 0 0 8px; }
        table { border-collapse: collapse; width: 100%; }
        tr { page-break-inside: avoid; }
        th, td { border: 1px solid #000; padding: 2px 8px; text-align: center; white-space: nowrap; }
        th { font-weight: 700; background: #eee; height: 12mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        td.l { text-align: left; } td.r { text-align: right; }
        td.sign { width: 3.5in; }
        tr.sum td { font-weight: 700; }
      </style></head><body>${pages.join("")}</body></html>`;
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open(); doc.write(html); doc.close();
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => iframe.remove(), 1000);
      }, 200);
    };

    return (
      <div style={{ overflow: "auto", maxHeight: "78vh", border: "1px solid #e5e7eb", borderRadius: 8, fontFamily: "'Times New Roman', Times, serif" }}>
        <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontWeight: 700, background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
          <span>
            CHẤM CÔNG DẠY VĂN HOÁ — Tuần {dayDM(startDate, 0)}–{dayDM(startDate, 5)} ({headerNote})
            <span style={{ display: "block", fontWeight: 400, fontSize: 12, color: run && run.published && run.publishMode === "demo" ? "#92400e" : "#6b7280" }}>Bản TKB: {banInfo}</span>
          </span>
          <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button type="button" onClick={() => printChamCong("current")} style={exportBtn} title="In đúng tab đang xem">🖨 In</button>
            <button type="button" onClick={() => printChamCong("all")} style={exportBtn} title="In Tổng cả tuần + từng buổi, mỗi buổi 1 trang">🖨 In cả tuần</button>
            <button type="button" onClick={exportExcel} style={exportBtn}>⬇ Xuất Excel</button>
          </span>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", overflowX: "auto", padding: "8px 10px", borderBottom: "1px solid #e5e7eb", background: "#fff" }}>
          <button type="button" onClick={() => setChamcongTab("total")} style={chamcongTab === "total" ? tabBtnActive : tabBtnInactive}>Tổng cả tuần</button>
          {buoiList.map((b) => (
            <button key={b.key} type="button" onClick={() => setChamcongTab(b.key)} style={chamcongTab === b.key ? tabBtnActive : tabBtnInactive}>{b.label}</button>
          ))}
        </div>
        {chamcongTab === "total" || !activeBuoi ? (
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>
              {useNewPayroll ? (
                <tr>
                  <th style={{ ...th, width: 44 }}>STT</th>
                  <th style={{ ...th, textAlign: "left" }}>Họ và tên</th>
                  <th style={th}>Bộ môn</th>
                  <th style={th}>Loại</th>
                  <th style={th}>Tiết thường</th>
                  <th style={th}>Tiết TC</th>
                  <th style={th}>Tiền tiết thường</th>
                  <th style={th}>Tiền tiết TC</th>
                  <th style={th}>Tổng tiền</th>
                </tr>
              ) : (
                <tr>
                  <th style={{ ...th, width: 44 }}>STT</th>
                  <th style={{ ...th, textAlign: "left" }}>Họ và tên</th>
                  <th style={th}>Bộ môn</th>
                  <th style={th}>Loại</th>
                  <th style={th}>Số tiết</th>
                  <th style={th}>Đơn giá</th>
                  <th style={th}>Thành tiền</th>
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((r, i) => useNewPayroll ? (
                <tr key={r.name}>
                  <td style={{ ...td }}>{i + 1}</td>
                  <td style={{ ...tdName }}>{r.name}</td>
                  <td style={td}>{r.bomon}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{r.loai}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.tietThuong}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.tietTC}</td>
                  <td style={{ ...td, textAlign: "right" }}>{r.tienThuong.toLocaleString("vi-VN")}</td>
                  <td style={{ ...td, textAlign: "right" }}>{r.tienTC.toLocaleString("vi-VN")}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{r.tien.toLocaleString("vi-VN")}</td>
                </tr>
              ) : (
                <tr key={r.name}>
                  <td style={{ ...td }}>{i + 1}</td>
                  <td style={{ ...tdName }}>{r.name}</td>
                  <td style={td}>{r.bomon}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{r.loai}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.tiet}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{UNIT_PRICE_OLD.toLocaleString("vi-VN")}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{r.tien.toLocaleString("vi-VN")}</td>
                </tr>
              ))}
              {useNewPayroll ? (
                <tr>
                  <td colSpan={4} style={{ ...tdName, background: "#f9fafb", textAlign: "right" }}>Tổng</td>
                  <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{sumTietThuong}</td>
                  <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{sumTietTC}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, background: "#f9fafb" }}>{sumTienThuong.toLocaleString("vi-VN")}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, background: "#f9fafb" }}>{sumTienTC.toLocaleString("vi-VN")}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, background: "#cffafe" }}>{sumTien.toLocaleString("vi-VN")}</td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} style={{ ...tdName, background: "#f9fafb", textAlign: "right" }}>Tổng</td>
                  <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{sumTiet}</td>
                  <td style={{ ...td, background: "#f9fafb" }}></td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, background: "#cffafe" }}>{sumTien.toLocaleString("vi-VN")}</td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>
              {useNewPayroll ? (
                <tr>
                  <th style={{ ...th, width: 44 }}>STT</th>
                  <th style={{ ...th, textAlign: "left" }}>Họ và tên</th>
                  <th style={th}>Bộ môn</th>
                  <th style={th}>Lớp</th>
                  <th style={th}>Hình thức</th>
                  <th style={th}>Số tiết</th>
                  <th style={th}>Loại tiết</th>
                  <th style={th}>Đơn giá</th>
                  <th style={th}>Thành tiền</th>
                </tr>
              ) : (
                <tr>
                  <th style={{ ...th, width: 44 }}>STT</th>
                  <th style={{ ...th, textAlign: "left" }}>Họ và tên</th>
                  <th style={th}>Bộ môn</th>
                  <th style={th}>Lớp</th>
                  <th style={th}>Số tiết</th>
                  <th style={th}>Hình thức</th>
                  <th style={th}>Đơn giá</th>
                  <th style={th}>Thành tiền</th>
                </tr>
              )}
            </thead>
            <tbody>
              {slotRows.map((r, i) => useNewPayroll ? (
                <tr key={r.name + r.lop + r.loaiTiet}>
                  <td style={{ ...td }}>{i + 1}</td>
                  <td style={{ ...tdName }}>{r.name}</td>
                  <td style={td}>{r.bomon}</td>
                  <td style={td}>{r.lop}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{r.loai}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.soTiet}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{r.loaiTiet}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{r.donGia.toLocaleString("vi-VN")}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{r.thanhTien.toLocaleString("vi-VN")}</td>
                </tr>
              ) : (
                <tr key={r.name + r.lop}>
                  <td style={{ ...td }}>{i + 1}</td>
                  <td style={{ ...tdName }}>{r.name}</td>
                  <td style={td}>{r.bomon}</td>
                  <td style={td}>{r.lop}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.tiet}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{r.loai}</td>
                  <td style={{ ...td, color: "#6b7280" }}>{UNIT_PRICE_OLD.toLocaleString("vi-VN")}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{r.tien.toLocaleString("vi-VN")}</td>
                </tr>
              ))}
              {useNewPayroll ? (
                <tr>
                  <td colSpan={5} style={{ ...tdName, background: "#f9fafb", textAlign: "right" }}>Tổng</td>
                  <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{slotSumSoTiet}</td>
                  <td style={{ ...td, background: "#f9fafb" }}></td>
                  <td style={{ ...td, background: "#f9fafb" }}></td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, background: "#cffafe" }}>{slotSumThanhTien.toLocaleString("vi-VN")}</td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} style={{ ...tdName, background: "#f9fafb", textAlign: "right" }}>Tổng</td>
                  <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{slotSumTiet}</td>
                  <td style={{ ...td, background: "#f9fafb" }}></td>
                  <td style={{ ...td, background: "#f9fafb" }}></td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, background: "#cffafe" }}>{slotSumTien.toLocaleString("vi-VN")}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ================= TKB GIÁO VIÊN / HỌC SINH (chọn 1) =================
  const isTeacher = mode === "teacher";
  const hideTeacher = mode === "studentOnly";
  const showRoomRow = hasRooms && !isTeacher && !!classById[selectedId];
  const options = isTeacher ? teachersWithLessons : displayClasses;
  const selCls = classById[selectedId];
  const selectedTeacherRec = teachers.find((t) => t.id === selectedId);
  const title = isTeacher
    ? `TKB GV: ${teacherName[selectedId] || ""}${selectedTeacherRec && selectedTeacherRec.employmentType ? ` (${selectedTeacherRec.employmentType})` : ""}`
    : `TKB ${selCls ? selCls.name : ""}${selCls && selCls.classType ? ` • ${selCls.classType}` : ""}`;
  // GV tự xem (lockTeacher) nhưng không có tiết nào thật sự (cả văn hoá
  // trong grid lẫn nghề theo tên) -> bản xếp/tuần này họ không có tiết, không
  // phải lỗi hiển thị nhầm người. (Không dùng teachersWithLessons để check vì
  // list đó đã luôn cộng thêm chính GV đang đăng nhập, nên phải soi thẳng.)
  const selT = teachers.find((t) => t.id === selectedId);
  const lockedNoLessons =
    isTeacher && lockTeacher &&
    !Object.values(grid).some((v) => v && v[1] === selectedId) &&
    !(selT && Object.keys(ngheByTeacherName).some((k) => k.startsWith(`${norm(selT.name)}|`)));

  // Nội dung 1 ô cho GV / HS tại (day, session, global).
  const cellFor = (day, si, g) => {
    if (isTeacher) {
      // quét lớp tìm tiết GV này dạy (chỉ văn hoá).
      for (const key of Object.keys(grid)) {
        const [classId, dd, pp] = key.split("|");
        if (grid[key] && Number(dd) === day && Number(pp) === g && grid[key][1] === selectedId) {
          const sn = subjectName[grid[key][0]] || "";
          // Nhãn "· Dạy thay" chỉ hiện khi ô CHƯA có ghi chú (periodNotes) — ô đã có ghi chú "Dạy thay: A → B..." thì ghi chú
          // đã nói rõ rồi, thêm nhãn nữa là lặp (yêu cầu: bớt nhãn Dạy thay).
          const hasNote = !!((periodNotes[classId] || {})[`${day}_${g}`]);
          const rm = roomOf(classId, day, si);
          return { a: ((classById[classId] || {}).name || classId) + (rm ? ` · P.${rm.label}` : ""), b: (isTc(grid[key]) ? `${sn} (TC)` : sn) + (!hasNote && isDayThay(classId, grid[key][0], selectedId) ? " · Dạy thay" : ""), classId };
        }
      }
      // GV dạy nghề: lịch nghề không có tiết cụ thể, chỉ có buổi -> phải soi
      // ĐÚNG ô "HN" thật của TỪNG LỚP trong grid tại (classId, day, g) — như
      // ngheAt() đã làm cho TKB học sinh — thay vì đoán cứng "tiết 1-4" (4
      // tiết nghề có thể nằm ĐẦU hay CUỐI buổi tuỳ lớp, không cố định, xem
      // ghi chú ngheAt phía trên).
      const rawHits = ngheForTeacher(selectedId, day, si);
      const ngheHits = (rawHits || []).filter((h) => {
        const cls = classes.find((c) => c.name === h.className);
        // Lớp không có TKB văn hoá (không có ô nào trong grid) -> hiện cả buổi nghề.
        if (!cls || !classIdsInGrid.has(cls.id)) return true;
        const cell = vhAt(cls.id, day, g);
        return cell && cell[0] === "HN";
      });
      if (ngheHits && ngheHits.length) {
        const h = ngheHits[0];
        return { a: h.className, b: (h.subject ? `HN: ${h.subject}` : "Học nghề") + (h.room ? ` • ${h.room}` : ""), kind: "nghe" };
      }
      return null;
    }
    // Học sinh: văn hoá + nghề + pháp luật. Đè thêm nháp chưa duyệt (nếu owner vừa xếp bù).
    const vh = vhAtEff(selectedId, day, g);
    if (vh && vh[0] !== "HN") {
      const [sid, tid] = vh;
      const sn = subjectName[sid] || sid;
      return { a: isTc(vh) ? `${sn} (TC)` : sn, b: hideTeacher ? "" : (tid ? teacherName[tid] || "" : "") };
    }
    const n = selCls && ngheAt(selCls.name, day, si, vh);
    if (n) return { a: (n.subject ? `HN: ${n.subject}` : "Học nghề"), b: hideTeacher ? "" : [n.teacher, n.room].filter(Boolean).join(" • "), kind: "nghe" };
    return null;
  };

  const exportExcel = () => {
    const header = ["Buổi", "Tiết", ...DAYS.map((d, i) => `${d} (${dayDM(startDate, i)})`)];
    const aoa = [header];
    sessions.forEach((ses, si) => {
      if (showRoomRow) aoa.push([ses.label || (si === 0 ? "Sáng" : "Chiều"), "Phòng", ...DAYS.map((_, day) => (roomOf(selectedId, day, si) || {}).label || "")]);
      ses.periods.forEach((p, ti) => {
        const row = [ti === 0 && !showRoomRow ? (ses.label || (si === 0 ? "Sáng" : "Chiều")) : "", `Tiết ${ti + 1} (${p.time})`];
        DAYS.forEach((_, day) => {
          const cell = cellFor(day, si, p.global);
          row.push(cell ? cell.a + (cell.b ? ` - ${cell.b}` : "") : "");
        });
        aoa.push(row);
      });
    });
    downloadAoa(aoa, "TKB", `tkb_${slug(title)}.xlsx`);
  };

  // Nội dung 1 ô cho 1 lớp bất kỳ (dùng khi xuất toàn trường, không phụ
  // thuộc lớp đang chọn trên dropdown như cellFor).
  const cellForClass = (classId, cls, day, si, g) => {
    const vh = vhAt(classId, day, g);
    if (vh && vh[0] !== "HN") {
      const [sid, tid] = vh;
      const sn = subjectName[sid] || sid;
      return { a: isTc(vh) ? `${sn} (TC)` : sn, b: hideTeacher ? "" : (tid ? teacherName[tid] || "" : "") };
    }
    const n = cls && ngheAt(cls.name, day, si, vh);
    if (n) return { a: (n.subject ? `HN: ${n.subject}` : "Học nghề"), b: hideTeacher ? "" : [n.teacher, n.room].filter(Boolean).join(" • ") };
    return null;
  };

  const exportAllExcel = () => {
    const wb = XLSX.utils.book_new();
    let count = 0;
    displayClasses.forEach((cls) => {
      const header = ["Buổi", "Tiết", ...DAYS.map((d, i) => `${d} (${dayDM(startDate, i)})`)];
      const aoa = [header];
      let hasAny = false;
      sessions.forEach((ses, si) => {
        ses.periods.forEach((p, ti) => {
          const row = [ti === 0 ? (ses.label || (si === 0 ? "Sáng" : "Chiều")) : "", `Tiết ${ti + 1} (${p.time})`];
          DAYS.forEach((_, day) => {
            const cell = cellForClass(cls.id, cls, day, si, p.global);
            if (cell) hasAny = true;
            row.push(cell ? cell.a + (cell.b ? ` - ${cell.b}` : "") : "");
          });
          aoa.push(row);
        });
      });
      if (!hasAny) return; // lớp không có TKB -> bỏ qua, không tạo sheet trống
      count++;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), safeSheetName(cls.name));
    });
    if (count === 0) {
      window.alert("Không có lớp nào có thời khoá biểu để xuất.");
      return;
    }
    downloadWorkbook(wb, "tkb_toan_truong_hoc_sinh.xlsx");
  };

  // Tổng kết tiết cho GV đang xem (mode "teacher"): mỗi lớp GV này dạy trong
  // bản xếp này -> thực tế (đếm từ grid) vs cần (weekSubjectPlan = "Cấu hình
  // môn học" đã áp dụng cho tuần), rồi 1 dòng Tổng cộng cho cả tuần.
  // Các môn "ảo" không thuộc Cấu hình môn học (không có "cần" để so sánh) —
  // loại khỏi thống kê thực tế/cần để không làm sai lệch tổng.
  const EXCLUDED_FROM_SUMMARY = new Set(["Nghỉ", "HN"]);
  const isExcludedSubject = (sid) => EXCLUDED_FROM_SUMMARY.has(sid) || ["Chào cờ", "Sinh hoạt", "HĐTN(CC)", "HĐTN(SH)"].includes(subjectName[sid]);
  let teacherSummary = null;
  if (isTeacher && selectedId) {
    const byClass = {}; // classId -> { actual, subjects: { subjectId: { actual, need } } }
    Object.entries(grid).forEach(([key, val]) => {
      if (!val || val[1] !== selectedId) return;
      const [classId] = key.split("|");
      const sid = val[0];
      if (isExcludedSubject(sid)) return;
      const e = (byClass[classId] = byClass[classId] || { actual: 0, subjects: {} });
      e.actual += 1;
      const se = (e.subjects[sid] = e.subjects[sid] || { actual: 0, need: (plan[classId] || {})[sid] || 0 });
      se.actual += 1;
    });
    const rows = Object.entries(byClass)
      .map(([cid, e]) => {
        const need = Object.values(e.subjects).reduce((s, x) => s + x.need, 0);
        const detail = Object.entries(e.subjects)
          .map(([sid, se]) => `${subjectName[sid] || sid} ${se.actual}/${se.need || "—"}`)
          .join(", ");
        return { classId: cid, className: (classById[cid] || {}).name || cid, actual: e.actual, need, detail };
      })
      .sort((a, b) => a.className.localeCompare(b.className, "vi", { numeric: true }));
    const totalActual = rows.reduce((s, r) => s + r.actual, 0);
    const totalNeed = rows.reduce((s, r) => s + r.need, 0);
    teacherSummary = { rows, totalActual, totalNeed };
  }

  // Danh sách lớp GV này dạy, gộp theo khối (vd "Khối 11: 11A5+11A6+...") —
  // chỉ admin xem (isOwner), lấy từ đúng các lớp trong teacherSummary.rows
  // (đã loại các lớp chỉ có Sinh hoạt/Chào cờ).
  let classesByKhoi = null;
  if (isOwner && teacherSummary && teacherSummary.rows.length > 0) {
    const byKhoi = {};
    teacherSummary.rows.forEach((r) => {
      const m = r.className.match(/^(\d{1,2})A/);
      const khoi = m ? Number(m[1]) : 0;
      (byKhoi[khoi] = byKhoi[khoi] || []).push(r.className);
    });
    classesByKhoi = Object.entries(byKhoi)
      .map(([k, names]) => ({ khoi: Number(k), names }))
      .sort((a, b) => b.khoi - a.khoi);
  }

  // Thống kê tiết cho lớp đang xem (mode "student"/"studentOnly"): mỗi môn
  // trong Phân công chuyên môn của lớp -> thực tế (đếm từ grid + nháp chưa
  // duyệt) vs cần (weekSubjectPlan), cộng số ô trống đúng buổi văn hoá còn
  // chưa xếp gì (loại nghỉ/nghề nhờ vhDistByClassName).
  let classSummary = null;
  if (!isTeacher && selCls) {
    const gridEff = { ...grid, ...draft };
    const rows = (selCls.subjects || [])
      .map((s) => {
        let actual = 0;
        Object.entries(gridEff).forEach(([key, val]) => {
          if (val && val[0] === s.subjectId && key.startsWith(`${selectedId}|`)) actual += 1;
        });
        const need = (plan[selectedId] || {})[s.subjectId] || 0;
        return { subjectId: s.subjectId, subjectName: subjectName[s.subjectId] || s.subjectId, actual, need, dev: actual - need };
      })
      .filter((r) => r.need > 0 || r.actual > 0)
      .sort((a, b) => a.subjectName.localeCompare(b.subjectName, "vi"));
    let emptyCount = 0;
    for (let day = 0; day < 6; day++) {
      sessions.forEach((ses, si) => {
        if (!isVanhoaSlot(selCls.name, day, si)) return;
        ses.periods.forEach((p) => {
          if (!gridEff[`${selectedId}|${day}|${p.global}`]) emptyCount += 1;
        });
      });
    }
    classSummary = {
      rows,
      emptyCount,
      totalActual: rows.reduce((s, r) => s + r.actual, 0),
      totalNeed: rows.reduce((s, r) => s + r.need, 0),
    };
  }
  const shortfallSubjects = classSummary ? classSummary.rows.filter((r) => r.dev < 0) : [];
  const draftCount = Object.keys(draft).length;
  const canFillShortfall = isOwner && mode === "student" && classSummary;

  const openFillDialog = () => {
    setFillSubjectId(shortfallSubjects[0] ? shortfallSubjects[0].subjectId : "");
    setFillTeacherId("");
    setFillDialogOpen(true);
  };

  // Tự tìm ô trống đúng buổi văn hoá + GV rảnh (không khai bận, không dạy lớp
  // khác giờ đó) + chưa chạm trần tiết/ngày của môn -> ghi thẳng vào nháp
  // (giống cơ chế "Sửa GV" toàn trường) cho tới khi đủ số tiết còn thiếu.
  const runAutoFill = async () => {
    if (!fillSubjectId || !fillTeacherId || !selCls || !classSummary) return;
    const row = classSummary.rows.find((r) => r.subjectId === fillSubjectId);
    const shortfall = row ? -row.dev : 0;
    if (shortfall <= 0) { setFillDialogOpen(false); return; }
    setFillBusy(true);
    try {
      // Trần tiết/ngày: ưu tiên cấu hình riêng theo tuần (weekMaxPerDay), mặc định 5 — khớp đúng
      // thứ tự ưu tiên bên solver (happyschool-api/src/routes/timetable.js).
      const maxPerDay = Math.max(1, Number((weekMaxPerDay[selectedId] || {})[fillSubjectId]) || 6);
      const gridEff = { ...grid, ...draft };
      const countOnDay = (day) => {
        let n = 0;
        Object.entries(gridEff).forEach(([key, val]) => {
          if (!val || val[0] !== fillSubjectId) return;
          const [cid, d] = key.split("|");
          if (cid === selectedId && Number(d) === day) n += 1;
        });
        return n;
      };
      const teacherBusyElsewhere = (day, g) =>
        Object.entries(gridEff).some(([key, val]) => val && val[1] === fillTeacherId && key.endsWith(`|${day}|${g}`));
      const candidates = [];
      for (let day = 0; day < 6; day++) {
        sessions.forEach((ses, si) => {
          if (!isVanhoaSlot(selCls.name, day, si)) return;
          ses.periods.forEach((p) => {
            const g = p.global;
            const key = `${selectedId}|${day}|${g}`;
            if (gridEff[key]) return;
            if (isTeacherBusyAt(fillTeacherId, day, g)) return;
            if (teacherBusyElsewhere(day, g)) return;
            candidates.push({ day, g });
          });
        });
      }
      const dayCounts = {};
      for (let d = 0; d < 6; d++) dayCounts[d] = countOnDay(d);
      const chosen = [];
      for (const c of candidates) {
        if (chosen.length >= shortfall) break;
        if (dayCounts[c.day] >= maxPerDay) continue;
        chosen.push(c);
        dayCounts[c.day] += 1;
      }
      if (chosen.length === 0) {
        window.alert("Không tìm được ô trống phù hợp (đúng buổi văn hoá + GV rảnh + chưa chạm trần tiết/ngày).");
        return;
      }
      for (const c of chosen) {
        await updateTimetableRunCell(schoolId, runId, { classId: selectedId, day: c.day, period: c.g, teacherId: fillTeacherId, subjectId: fillSubjectId });
      }
      const dr = await getTimetableRunDraft(schoolId, runId);
      setDraft(dr || {});
      window.alert(`Đã xếp thêm ${chosen.length}/${shortfall} tiết vào bản nháp — bấm "✔ Duyệt" bên dưới để áp dụng chính thức.`);
      setFillDialogOpen(false);
      setFillSubjectId("");
      setFillTeacherId("");
    } catch (e) {
      window.alert(e.message || "Có lỗi khi lưu.");
    } finally {
      setFillBusy(false);
    }
  };

  const approveClassDraft = async () => {
    if (!window.confirm(`Duyệt ${draftCount} ô đã sửa thành 1 bản (version) TKB mới? Bản hiện tại vẫn được giữ nguyên.`)) return;
    setApproving(true);
    try {
      const { newRunId } = await approveTimetableRunDraft(schoolId, runId);
      window.alert(`Đã duyệt — tạo bản mới (runId ${newRunId}). Vào lại danh sách bản xếp để chọn bản mới nhất.`);
      setDraft({});
    } catch (e) {
      window.alert(e.message || "Không duyệt được.");
    } finally {
      setApproving(false);
    }
  };
  const discardClassDraft = async () => {
    if (!window.confirm(`Bỏ qua ${draftCount} ô đã lưu (chưa duyệt)? Sẽ quay lại đúng như bản gốc.`)) return;
    setSaving(true);
    try {
      await discardTimetableRunDraft(schoolId, runId);
      setDraft({});
    } catch (e) {
      window.alert(e.message || "Không bỏ được bản nháp.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        {!lockTeacher && (
          <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ padding: 8, minWidth: 220 }}>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}{isTeacher && o.employmentType ? ` (${o.employmentType})` : ""}
              </option>
            ))}
          </select>
        )}
        <strong style={{ fontSize: 15 }}>{title}</strong>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {mode === "studentOnly" && (
            <button type="button" onClick={exportAllExcel} style={exportBtn}>⬇ Xuất Toàn Trường</button>
          )}
          <button type="button" onClick={exportExcel} style={exportBtn}>⬇ Xuất Excel</button>
        </div>
      </div>
      {classesByKhoi && classesByKhoi.length > 0 && (
        <div style={{ marginBottom: 10, fontSize: 13, color: "#374151" }}>
          {classesByKhoi.map((g) => (
            <div key={g.khoi}>Khối {g.khoi}: {g.names.join("+")}</div>
          ))}
        </div>
      )}
      {isOwner && teacherSummary && teacherSummary.rows.length > 0 && (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 10 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Lớp</th>
                <th style={{ ...th, textAlign: "left" }}>Chi tiết môn (thực tế/cần)</th>
                <th style={th}>Thực tế</th>
                <th style={th}>Cần</th>
                <th style={th}>Thừa/Thiếu</th>
              </tr>
            </thead>
            <tbody>
              {teacherSummary.rows.map((r) => {
                const dev = r.actual - r.need;
                return (
                  <tr key={r.classId}>
                    <td style={{ ...tdName }}>{r.className}</td>
                    <td style={{ ...td, textAlign: "left" }}>{r.detail}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{r.actual}</td>
                    <td style={td}>{r.need || "—"}</td>
                    <td style={{ ...td, fontWeight: 600, color: dev > 0 ? "#dc2626" : dev < 0 ? "#b45309" : "#059669" }}>
                      {dev > 0 ? `+${dev}` : dev}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={2} style={{ ...td, fontWeight: 700, background: "#f9fafb", textAlign: "right" }}>Tổng cộng cả tuần</td>
                <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{teacherSummary.totalActual}</td>
                <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{teacherSummary.totalNeed || "—"}</td>
                <td style={{ ...td, fontWeight: 700, background: "#f9fafb", color: teacherSummary.totalActual - teacherSummary.totalNeed > 0 ? "#dc2626" : teacherSummary.totalActual - teacherSummary.totalNeed < 0 ? "#b45309" : "#059669" }}>
                  {(() => { const d = teacherSummary.totalActual - teacherSummary.totalNeed; return d > 0 ? `+${d}` : d; })()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {isOwner && classSummary && classSummary.rows.length > 0 && (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 10 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Môn</th>
                <th style={th}>Đã có</th>
                <th style={th}>Cần</th>
                <th style={th}>Đủ/Thiếu</th>
              </tr>
            </thead>
            <tbody>
              {classSummary.rows.map((r) => (
                <tr key={r.subjectId}>
                  <td style={{ ...tdName }}>{r.subjectName}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.actual}</td>
                  <td style={td}>{r.need || "—"}</td>
                  <td style={{ ...td, fontWeight: 600, color: r.dev > 0 ? "#dc2626" : r.dev < 0 ? "#b45309" : "#059669" }}>
                    {r.dev > 0 ? `Thừa ${r.dev}` : r.dev < 0 ? `Thiếu ${-r.dev}` : "Đủ"}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ...td, fontWeight: 700, background: "#f9fafb", textAlign: "right" }}>Tổng cộng cả tuần</td>
                <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{classSummary.totalActual}</td>
                <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{classSummary.totalNeed || "—"}</td>
                <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>Ô trống: {classSummary.emptyCount}</td>
              </tr>
            </tbody>
          </table>
          {canFillShortfall && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderTop: "1px solid #e5e7eb", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={openFillDialog}
                disabled={shortfallSubjects.length === 0}
                style={shortfallSubjects.length ? editBtnActive : editBtn}
              >
                🔧 Xếp bù tiết thiếu {shortfallSubjects.length ? `(${shortfallSubjects.length} môn)` : ""}
              </button>
              {draftCount > 0 && (
                <>
                  <span style={{ fontSize: 12, color: "#b45309" }}>⚠ Có {draftCount} ô đã lưu, chưa duyệt (toàn trường)</span>
                  <button type="button" onClick={discardClassDraft} disabled={saving} style={{ ...editBtn, color: "#dc2626", borderColor: "#fca5a5" }}>
                    🗑 Bỏ qua nháp
                  </button>
                  <button type="button" onClick={approveClassDraft} disabled={approving} style={{ ...editBtnActive, background: "#0ea5a3" }}>
                    {approving ? "Đang duyệt..." : "✔ Duyệt"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {fillDialogOpen && classSummary && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 20, width: 380, maxWidth: "92vw" }}>
            <h4 style={{ margin: "0 0 12px" }}>🔧 Xếp bù tiết thiếu — {selCls && selCls.name}</h4>
            <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Môn đang thiếu tiết</label>
            <select
              value={fillSubjectId}
              onChange={(e) => { setFillSubjectId(e.target.value); setFillTeacherId(""); }}
              style={{ width: "100%", padding: 8, marginBottom: 12, border: "1px solid #d1d5db", borderRadius: 6 }}
            >
              <option value="">— chọn môn —</option>
              {shortfallSubjects.map((r) => (
                <option key={r.subjectId} value={r.subjectId}>{r.subjectName} (thiếu {-r.dev} tiết)</option>
              ))}
            </select>
            {fillSubjectId && (
              <>
                <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Giáo viên</label>
                <select
                  value={fillTeacherId}
                  onChange={(e) => setFillTeacherId(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 12, border: "1px solid #d1d5db", borderRadius: 6 }}
                >
                  <option value="">— chọn GV —</option>
                  {teacherCandidatesFor(selectedId, fillSubjectId).map((tid) => (
                    <option key={tid} value={tid}>{teacherName[tid] || tid}</option>
                  ))}
                </select>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <button type="button" onClick={() => setFillDialogOpen(false)} disabled={fillBusy} style={editBtn}>Huỷ</button>
              <button type="button" onClick={runAutoFill} disabled={fillBusy || !fillSubjectId || !fillTeacherId} style={editBtnActive}>
                {fillBusy ? "Đang lưu..." : "💾 Lưu"}
              </button>
            </div>
          </div>
        </div>
      )}
      {mode === "student" && selectedId && (isAdmin || myStaffId) && (
        <p style={{ margin: "0 0 10px", padding: "8px 12px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, color: "#065f46", fontSize: 13 }}>
          ⇄ <b>Đổi tiết:</b> {isAdmin ? "bấm vào một tiết bất kỳ của lớp" : "bấm vào tiết của bạn (ô viền xanh)"} để đổi với 1 tiết khác trong lớp này.
        </p>
      )}
      {lockedNoLessons && (
        <p style={{ margin: "0 0 10px", padding: "8px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, color: "#92400e", fontSize: 13 }}>
          Bạn không có tiết nào trong bản thời khoá biểu này (tuần/bản xếp hiện tại chưa phân công tiết cho bạn).
        </p>
      )}
      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
          <thead>
            <tr>
              <th style={{ ...thFix(0), minWidth: 60, zIndex: 6 }}>Buổi</th>
              <th style={{ ...thFix(60), minWidth: 56, zIndex: 6 }}>Tiết</th>
              {DAYS.map((d, i) => (<th key={d} style={{ ...th, minWidth: 120 }}>{d}<br /><span style={{ fontWeight: 400, color: "#6b7280" }}>({dayDM(startDate, i)})</span></th>))}
            </tr>
          </thead>
          <tbody>
            {sessions.map((ses, si) => (
              <React.Fragment key={si}>
              {showRoomRow && (
                <RoomRow classId={selectedId} si={si} rowSpan={ses.periods.length + 1} sesLabel={ses.label || (si === 0 ? "Sáng" : "Chiều")} roomOf={roomOf} cellStyle={td}
                  labelStyle={{ ...td, position: "sticky", left: 0, zIndex: 5, fontWeight: 700, background: "#f9fafb", verticalAlign: "middle", textTransform: "uppercase" }} />
              )}
              {ses.periods.map((p, ti) => (
                <tr key={`${si}-${ti}`} data-pi={periodShadeIndex(p.global)}>
                  {ti === 0 && !showRoomRow && (
                    <td rowSpan={ses.periods.length} style={{ ...td, position: "sticky", left: 0, zIndex: 5, fontWeight: 700, background: "#f9fafb", verticalAlign: "middle", textTransform: "uppercase" }}>{ses.label || (si === 0 ? "Sáng" : "Chiều")}</td>
                  )}
                  <td style={{ ...td, position: "sticky", left: 60, zIndex: 5, background: "#fff", whiteSpace: "nowrap" }}>Tiết {ti + 1}<div style={{ color: "#9ca3af", fontSize: 10 }}>{p.time}</div></td>
                  {DAYS.map((d, day) => {
                    const cell = cellFor(day, si, p.global);
                    const busy = isTeacher && isTeacherBusyAt(selectedId, day, p.global);
                    // Đã xếp (do xếp tay/ghim) NHƯNG GV báo bận đúng giờ đó -> tô tím
                    // để dễ soát, phân biệt với ô bình thường hay ô trống-bận (đỏ).
                    const conflictBusy = !!cell && cell.kind !== "nghe" && busy;
                    // Ghi chú riêng của ô (periodNotes): của đúng lớp đang dạy ở ô này,
                    // hoặc gắn thẳng với GV đang xem (vd ô đã cắt "Lên lớp không dạy").
                    const noteKey = `${day}_${p.global}`;
                    const noteLines = [];
                    if (isTeacher) {
                      if (cell && cell.classId) {
                        const r = (periodNotes[cell.classId] || {})[noteKey];
                        if (r) noteLines.push(r.note);
                      }
                      Object.entries(periodNotes).forEach(([cid, m]) => {
                        const r = m && m[noteKey];
                        if (r && r.teacherId === selectedId && !(cell && cell.classId === cid)) {
                          noteLines.push(`${(classById[cid] || {}).name || cid}: ${r.note}`);
                        }
                      });
                    }
                    const noteBlock = noteLines.length ? (
                      <div style={{ color: "#92400e", fontSize: 11, whiteSpace: "pre-wrap" }}>📝 {noteLines.join(" | ")}</div>
                    ) : null;
                    const rawSel = mode === "student" ? grid[`${selectedId}|${day}|${p.global}`] : null;
                    const isMineCell = !!(rawSel && myStaffId && rawSel[1] === myStaffId);
                    const canSwap = !!(rawSel && rawSel[0] && subjectName[rawSel[0]] && cell && cell.kind !== "nghe" && (isAdmin || isMineCell));
                    return (
                      <td
                        key={d}
                        onClick={canSwap ? () => setSwapTarget({ classId: selectedId, day, period: p.global }) : undefined}
                        onDoubleClick={isAdmin && isTeacher && cell && cell.classId && cell.kind !== "nghe" && grid[`${cell.classId}|${day}|${p.global}`] ? () => setSubTarget({ classId: cell.classId, day, period: p.global })
                          : isAdmin && isTeacher && !cell && !busy ? () => setAssignTarget({ teacherId: selectedId, day, period: p.global }) : undefined}
                        title={canSwap ? (isMineCell ? "Tiết của bạn — bấm để đổi tiết" : "Bấm để đổi tiết") : isAdmin && isTeacher && cell && cell.classId && cell.kind !== "nghe" ? "Nhấn đúp để thay GV / để trống / trông lớp" : isAdmin && isTeacher && !cell && !busy ? "Ô trống — nhấn đúp để điền môn và lớp" : undefined}
                        style={{ ...td, minWidth: 110, cursor: canSwap || (isAdmin && isTeacher && ((cell && cell.kind !== "nghe") || (!cell && !busy))) ? "pointer" : undefined, boxShadow: isMineCell && canSwap ? "inset 0 0 0 2px #10b981" : undefined, background: isMineCell && canSwap ? "#ecfdf5" : conflictBusy ? "#f3e8ff" : cell ? (cell.kind === "nghe" ? "#fef3c7" : "#eff6ff") : (noteBlock ? "#fef9c3" : busy ? "#fee2e2" : "#fff") }}
                      >
                        {noteBlock}
                        {cell ? (
                          <>
                            <div>{cell.a}</div>
                            {cell.b && (
                              <div style={{ color: conflictBusy ? "#7c3aed" : "#6b7280", fontSize: 11 }}>
                                {cell.b}{conflictBusy ? " ⚠️ Bận" : ""}
                              </div>
                            )}
                          </>
                        ) : (busy ? <div style={{ color: "#b91c1c", fontSize: 11 }}>Bận</div> : "")}
                      </td>
                    );
                  })}
                </tr>
              ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {assignTarget && (
        <HappySchoolCellAssignDialog
          schoolId={schoolId}
          runId={runId}
          weekId={weekId}
          target={assignTarget}
          grid={grid}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          onClose={() => setAssignTarget(null)}
          onSaved={() => { setAssignTarget(null); setReloadKey((k) => k + 1); }}
        />
      )}
      {swapTarget && (
        <HappySchoolCellSwapDialog
          schoolId={schoolId} runId={runId} target={swapTarget} grid={grid} classes={classes} subjects={subjects}
          teacherName={teacherName} sessions={sessions} isBusyAt={isTeacherBusyAt}
          sessionKind={(className, day, si) => { const row = vhDistByClassName[className]; return row && row.cells && row.cells[day] ? row.cells[day][si === 0 ? "sang" : "chieu"] || null : null; }}
          published={!!(run && run.published && run.publishMode !== "demo")}
          onClose={() => setSwapTarget(null)}
          onSaved={() => { setSwapTarget(null); setReloadKey((k) => k + 1); }}
        />
      )}
      {subTarget && (
        <HappySchoolCellSubstituteDialog
          schoolId={schoolId}
          runId={runId}
          weekId={weekId}
          target={subTarget}
          grid={grid}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          isBusyAt={isTeacherBusyAt}
          periodNotes={periodNotes}
          onClose={() => setSubTarget(null)}
          onSaved={() => { setSubTarget(null); setReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
};

const exportBtn = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const editBtn = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const editBtnActive = { ...editBtn, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const th = { border: "1px solid #e5e7eb", padding: "4px 6px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center", position: "sticky" };
const thFix = (left) => ({ ...th, position: "sticky", left, top: 0, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "3px 5px", textAlign: "center", verticalAlign: "top" };
const tdName = { ...td, fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" };
const tabBtnInactive = { padding: "6px 12px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 };
const tabBtnActive = { ...tabBtnInactive, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };

export default HappySchoolTimetableVersion;
